/**
 * CLIENTE DE LA API
 *
 * Un solo sitio por donde sale una petición. Eso permite tres cosas que si
 * no acaban repetidas en veinte componentes: poner el token, dar al error
 * de red la misma forma que al error de la API, y detectar la sesión
 * caducada en un único punto.
 *
 * LA UI NO DECIDE NADA
 *
 * Aquí no hay reglas de negocio y no debe haberlas. Si una pantalla
 * necesita saber si algo se puede confirmar, lo pregunta a la API; no lo
 * recalcula. Las reglas viven en la base y en los servicios, y tener una
 * segunda copia en el navegador es tener dos verdades.
 */

export interface ErrorApi {
  readonly code: string;
  readonly message: string;
  readonly details?: unknown;
  readonly fieldErrors?: Record<string, string>;
  readonly requestId?: string;
}

export class FalloApi extends Error {
  readonly codigo: string;
  readonly estado: number;
  readonly erroresDeCampo: Record<string, string> | undefined;
  readonly requestId: string | undefined;
  /** FASE 9.2: datos estructurados del error (p. ej. disponible/solicitado en STOCK_INSUFICIENTE). */
  readonly detalles: unknown;

  constructor(estado: number, cuerpo: ErrorApi) {
    super(cuerpo.message);
    this.name = 'FalloApi';
    this.estado = estado;
    this.codigo = cuerpo.code;
    this.erroresDeCampo = cuerpo.fieldErrors;
    this.requestId = cuerpo.requestId;
    this.detalles = cuerpo.details;
  }
}

export const CLAVE_TOKEN = 'neatos.token';

export function tokenGuardado(): string | null {
  try {
    return localStorage.getItem(CLAVE_TOKEN);
  } catch {
    return null;
  }
}

export function guardarToken(token: string | null): void {
  try {
    if (token === null) localStorage.removeItem(CLAVE_TOKEN);
    else localStorage.setItem(CLAVE_TOKEN, token);
  } catch {
    /* Navegador con almacenamiento bloqueado: la sesión durará lo que la pestaña. */
  }
}

/**
 * F14.1 — ¿llegó la petición al servidor? `true` con cualquier respuesta HTTP de la API; `false` cuando `fetch` no
 * obtuvo respuesta. Lo escucha el monitor de conexión (`pwa.ts`) para no tener que preguntar al servidor si otra
 * petición ya lo ha hecho.
 */
const observadoresRed = new Set<(llego: boolean) => void>();
export function observarRed(f: (llego: boolean) => void): () => void {
  observadoresRed.add(f);
  return () => observadoresRed.delete(f);
}
function avisarRed(llego: boolean): void {
  for (const f of observadoresRed) f(llego);
}

/** Se avisa cuando la sesión deja de servir, para que el shell mande al login. */
type Escucha = () => void;
const alCaducar = new Set<Escucha>();
export function cuandoCaduqueLaSesion(f: Escucha): () => void {
  alCaducar.add(f);
  return () => alCaducar.delete(f);
}

async function peticion<T>(
  ruta: string,
  opciones: RequestInit & { cuerpo?: unknown } = {},
): Promise<T> {
  const { cuerpo, ...resto } = opciones;
  const cabeceras = new Headers(resto.headers);
  const token = tokenGuardado();
  if (token !== null) cabeceras.set('authorization', `Bearer ${token}`);

  let body = resto.body;
  if (cuerpo !== undefined) {
    cabeceras.set('content-type', 'application/json');
    body = JSON.stringify(cuerpo);
  }

  let respuesta: Response;
  try {
    respuesta = await fetch(ruta, { ...resto, headers: cabeceras, body });
  } catch {
    avisarRed(false);
    //  Un fallo de red se presenta con la MISMA forma que un error de la
    //  API para que las pantallas no tengan dos caminos de error.
    throw new FalloApi(0, {
      code: 'SIN_CONEXION',
      message: 'No se ha podido contactar con el servidor. Comprueba tu conexión.',
    });
  }
  //  Un 502/503/504 lo devuelve el proxy con la aplicación caída: no es «el servidor respondió».
  avisarRed(respuesta.status < 502 || respuesta.status > 504);

  if (respuesta.status === 204) return undefined as T;

  const texto = await respuesta.text();
  const datos: unknown = texto === '' ? null : safeJson(texto);

  if (!respuesta.ok) fallo(respuesta.status, datos, ruta, token);

  return datos as T;
}

/**
 * ¿Este fallo significa que la sesión ya no sirve?
 *
 * Sólo un 401 de una petición AUTENTICADA con el token vigente (o enviada sin
 * token, porque ya no había). Nunca:
 *   - 403 (falta de permiso: la sesión está bien, se muestra «sin permiso»),
 *     404, 409, 422, 429, 5xx ni un fallo de red;
 *   - el 401 del propio login (CREDENCIALES_INVALIDAS: no había sesión);
 *   - el 401 de una petición que salió con un token ANTERIOR (otra pestaña o
 *     un nuevo login ya lo cambiaron): borrar entonces el token nuevo echaría
 *     a quien acaba de entrar.
 */
export function esSesionCaducada(estado: number, ruta: string, tokenEnviado: string | null): boolean {
  if (estado !== 401) return false;
  if (ruta.split('?')[0] === '/api/auth/login') return false;
  return tokenEnviado === null || tokenEnviado === tokenGuardado();
}

function fallo(estado: number, datos: unknown, ruta: string, tokenEnviado: string | null): never {
  const cuerpoError: ErrorApi =
    datos !== null && typeof datos === 'object' && 'code' in datos
      ? (datos as ErrorApi)
      : { code: 'ERROR', message: `Error ${String(estado)}.` };

  //  Sesión caducada o cerrada: un solo punto decide, y el shell
  //  reacciona. Si cada pantalla lo hiciera por su cuenta, unas
  //  redirigirían y otras se quedarían en blanco.
  if (esSesionCaducada(estado, ruta, tokenEnviado)) {
    guardarToken(null);
    for (const f of alCaducar) f();
  }
  throw new FalloApi(estado, cuerpoError);
}

/** F13.2: POST que devuelve un fichero (PDF de etiquetas). Errores con la misma forma que el resto. */
async function peticionBinaria(ruta: string, cuerpo: unknown): Promise<Blob> {
  const cabeceras = new Headers({ 'content-type': 'application/json' });
  const token = tokenGuardado();
  if (token !== null) cabeceras.set('authorization', `Bearer ${token}`);
  let respuesta: Response;
  try {
    respuesta = await fetch(ruta, { method: 'POST', headers: cabeceras, body: JSON.stringify(cuerpo) });
  } catch {
    throw new FalloApi(0, { code: 'SIN_CONEXION', message: 'No se ha podido contactar con el servidor. Comprueba tu conexión.' });
  }
  if (!respuesta.ok) fallo(respuesta.status, safeJson(await respuesta.text()), ruta, token);
  return respuesta.blob();
}

/**
 * F13.5-B: GET autenticado que devuelve un fichero (exportaciones). El nombre lo da el servidor
 * (`content-disposition`); nunca hay una URL pública del fichero.
 */
async function peticionFichero(ruta: string, senal?: AbortSignal): Promise<{ blob: Blob; nombre: string; sha256: string | null }> {
  const cabeceras = new Headers();
  const token = tokenGuardado();
  if (token !== null) cabeceras.set('authorization', `Bearer ${token}`);
  let respuesta: Response;
  try {
    //  PC-v2.2-C2.2: `senal` (opcional) cancela la descarga (p. ej. el Excel de unos filtros que ya no están en pantalla).
    respuesta = await fetch(ruta, senal === undefined ? { headers: cabeceras } : { headers: cabeceras, signal: senal });
  } catch {
    throw new FalloApi(0, { code: 'SIN_CONEXION', message: 'No se ha podido contactar con el servidor. Comprueba tu conexión.' });
  }
  if (!respuesta.ok) fallo(respuesta.status, safeJson(await respuesta.text()), ruta, token);
  const m = /filename="([^"]+)"/.exec(respuesta.headers.get('content-disposition') ?? '');
  return { blob: await respuesta.blob(), nombre: m?.[1] ?? 'exportacion', sha256: respuesta.headers.get('x-neatos-sha256') };
}

/**
 * F14.1 (AUTH-ESTAB-FETCH-DIRECTO) — GET autenticado de un fichero para abrirlo (documento, evidencia, PDF). Mismo
 * trato que el cliente HTTP: un 401 de sesión cerrada lleva al login como en cualquier otra petición
 * (`esSesionCaducada`); un 403/404/5xx o un fallo de red devuelven `null` («No se ha podido abrir») sin tocar la
 * sesión. Antes, cada pantalla hacía su `fetch` a mano y un 401 se quedaba en «No se ha podido abrir».
 */
export async function descargarConSesion(ruta: string): Promise<Blob | null> {
  const token = tokenGuardado();
  if (token === null) return null;
  let respuesta: Response;
  try {
    respuesta = await fetch(ruta, { headers: { authorization: `Bearer ${token}` } });
  } catch {
    avisarRed(false);
    return null;
  }
  avisarRed(respuesta.status < 502 || respuesta.status > 504);
  if (!respuesta.ok) {
    if (esSesionCaducada(respuesta.status, ruta, token)) {
      guardarToken(null);
      for (const f of alCaducar) f();
    }
    return null;
  }
  try {
    return await respuesta.blob();
  } catch {
    return null;
  }
}

function safeJson(t: string): unknown {
  try {
    return JSON.parse(t);
  } catch {
    return null;
  }
}

export const api = {
  /** `opciones.headers` (F14.2): sólo para la cabecera del perfil offline (`offline/identidad-sync.ts`). */
  get: <T,>(ruta: string, opciones?: { headers?: Record<string, string> }): Promise<T> => peticion<T>(ruta, { ...opciones }),
  /**
   * `opciones.signal` (FASE 5.7-C C01): sólo para el logout offline, que
   * necesita acotar cuánto puede tardar el aviso al backend sin bloquear el
   * cierre de sesión local. No se añade un timeout por defecto a las demás
   * llamadas — una subida de evidencia legítimamente puede tardar más.
   */
  post: <T,>(ruta: string, cuerpo?: unknown, opciones?: { signal?: AbortSignal; headers?: Record<string, string> }): Promise<T> =>
    peticion<T>(ruta, { method: 'POST', cuerpo, ...opciones }),
  patch: <T,>(ruta: string, cuerpo?: unknown): Promise<T> =>
    peticion<T>(ruta, { method: 'PATCH', cuerpo }),
  put: <T,>(ruta: string, cuerpo?: unknown): Promise<T> =>
    peticion<T>(ruta, { method: 'PUT', cuerpo }),
  del: (ruta: string): Promise<void> =>
    peticion<void>(ruta, { method: 'DELETE' }),
  /** Subida de fichero: `FormData`, sin `content-type` a mano. */
  subir: <T,>(ruta: string, datos: FormData, opciones?: { headers?: Record<string, string> }): Promise<T> =>
    peticion<T>(ruta, { method: 'POST', body: datos, ...opciones }),
  /** F13.2: descarga (POST con cuerpo JSON → fichero). */
  descargar: (ruta: string, cuerpo: unknown): Promise<Blob> => peticionBinaria(ruta, cuerpo),
  /** F13.5-B: descarga (GET → fichero con su nombre y SHA-256). */
  fichero: (ruta: string, senal?: AbortSignal): Promise<{ blob: Blob; nombre: string; sha256: string | null }> => peticionFichero(ruta, senal),
};

// ---------------------------------------------------------------- tipos

export interface Branding {
  nombre: string;
  nombreCorto: string;
  lema: string;
  organizacion: string;
  colorPrincipal: string;
  colorOscuro: string;
  logoUrl: string;
  faviconUrl: string;
  monograma: string;
}

export interface Usuario {
  id: string;
  email: string;
  nombre: string;
  apellidos: string;
  idCliente: string | null;
  /** true = debe cambiar su contraseña temporal antes de poder usar el resto de la aplicación. */
  requiereCambioPwd: boolean;
  roles: { rol: string; idCentro: string | null }[];
  permisos: string[];
  alcanceCentros: string[] | null;
}

/** Fila del listado de administración de usuarios (GET /api/usuarios). */
export interface UsuarioResumen {
  id: string;
  email: string;
  nombre: string;
  apellidos: string;
  activo: boolean;
  ultimoAcceso: string | null;
  /** null sólo de forma transitoria: todo usuario persistido tiene un rol activo. */
  rol: string | null;
  idOperario: string | null;
  operarioCodigo: string | null;
  operarioNombre: string | null;
  idCliente: string | null;
  clienteCodigo: string | null;
  clienteRazonSocial: string | null;
}

/** Ficha de un usuario (GET/POST/PATCH /api/usuarios). */
export interface UsuarioDetalle extends UsuarioResumen {
  telefono: string | null;
  requiereCambioPwd: boolean;
  creadoEn: string;
  intentosFallidos: number;
  /** NULL = sin bloqueo temporal vigente. */
  bloqueadoHasta: string | null;
  /** NULL = sin bloqueo manual. NOT NULL = un ADMIN bloqueó esta cuenta. */
  bloqueadoManualmenteEn: string | null;
  bloqueadoManualmentePor: string | null;
  motivoBloqueo: string | null;
}

/** Una fila del histórico de asignaciones de rol de un usuario (GET /api/usuarios/:id/roles). */
export interface HistoricoRol {
  rol: string;
  asignadoEn: string;
  asignadoPor: string | null;
  retiradoEn: string | null;
  retiradoPor: string | null;
  activo: boolean;
}

export interface Cliente {
  id: string;
  codigo: string;
  razonSocial: string;
  activo: boolean;
  centros: number;
  /** Barato a propósito (UX-POLISH-01, punto 1): sólo dice SI hay logo, nunca lo trae — ver `componentes/logo-remoto.ts`. */
  tieneLogo: boolean;
}

/** Devuelto por GET /api/clientes/:id/detalle y por POST/PATCH de cliente. */
export interface ClienteDetalle {
  id: string;
  codigo: string;
  razonSocial: string;
  nif: string;
  direccion: string | null;
  email: string | null;
  telefono: string | null;
  activo: boolean;
  /** `data:` URI ya resuelto (L99 punto 4), o `null` sin logo — Avatar cae a iniciales. */
  logoUrl: string | null;
}

export interface Centro {
  id: string;
  idCliente: string;
  codigo: string;
  descripcion: string;
  clienteCodigo: string;
  clienteRazonSocial: string;
  activo: boolean;
  diasOperativos: number[] | null;
  /**
   * Logo del CLIENTE dueño de este centro. Sólo `GET /api/centros/:id`
   * (una entidad) lo resuelve de verdad — en cualquier listado de centros
   * viene `null` a propósito, para no repetir el mismo logo en cada fila.
   */
  clienteLogoUrl: string | null;
  /** FASE 6.7-A: IANA ('Europe/Madrid') — para construir en el navegador el instante UTC de una hora LOCAL elegida, sin usar nunca la timezone del navegador. */
  zonaHoraria: string;
}

/** Devuelto por GET /api/centros/:id/detalle y por POST/PATCH de centro. */
export interface CentroDetalle {
  id: string;
  idCliente: string;
  codigo: string;
  descripcion: string;
  direccion: string | null;
  activo: boolean;
  diasOperativos: number[] | null;
}

/** Devuelto por GET/POST/PATCH de tipos de limpieza. */
export interface TipoLimpieza {
  id: string;
  idCliente: string;
  codigo: string;
  descripcion: string;
  /** Minutos estimados. null = sin estimación declarada. */
  duracionEstMin: number | null;
  activo: boolean;
}

/** Nodo del árbol de ubicaciones devuelto por GET/POST/PATCH. */
export interface Ubicacion {
  id: string;
  idPadre: string | null;
  /** SALA | ZONA | ELEMENTO | SUBELEMENTO (solo lectura, legacy). */
  tipo: string;
  codigo: string;
  descripcion: string;
  activo: boolean;
  orden: number;
  /** Profundidad en el árbol: SALA=0, hijo de SALA=1, etc. */
  nivel: number;
  /** true si algún nodo del mismo centro tiene éste como padre. */
  tieneHijos: boolean;
}

export interface ExcepcionCalendario {
  id: string;
  fecha: string;
  tipo: string;
  descripcion: string | null;
  /** true = festivo global/nacional, sólo lectura desde este módulo. */
  nacional: boolean;
  /** FASE 6.2/6.3: MANUAL o PLANTILLA — trazabilidad de origen, nunca condiciona si se puede borrar. */
  origen: string;
}

export interface Calendario {
  diasOperativos: number[] | null;
  excepciones: ExcepcionCalendario[];
}

// ------------------------------------------------------- FASE 6.3: festivos

/** Plantillas de festivos (FASE 6.2 §L / 6.3 §E) — catálogo global, sin centro. */
export interface PlantillaCalendario {
  id: string;
  codigo: string;
  nombre: string;
  descripcion: string | null;
  dias: number;
}

export interface DiaPlantilla {
  id: string;
  fecha: string;
  descripcion: string;
  tipo: string;
}

// -------------------------------------------------- FASE 6.3: disponibilidad

/** Patrón semanal de disponibilidad de planificación (FASE 6.1 §F). */
export interface IntervaloDisponibilidad {
  /** ISO 1=lunes..7=domingo. */
  diaSemana: number;
  horaInicio: string;
  horaFin: string;
}

export interface IntervaloDisponibilidadGuardado extends IntervaloDisponibilidad {
  id: string;
}

export type TipoExcepcionDisponibilidad = 'NO_DISPONIBLE' | 'DISPONIBILIDAD_EXTRA';

export interface ExcepcionDisponibilidad {
  id: string;
  fecha: string;
  tipo: TipoExcepcionDisponibilidad;
  horaInicio: string | null;
  horaFin: string | null;
}

// ------------------------------------------------------- FASE 6.3: bloqueos

/** Bloqueo operativo de centro/ubicación (FASE 6.1 §G) — nunca festivo. */
export interface Bloqueo {
  id: string;
  idCentro: string;
  idUbicacion: string | null;
  fechaDesde: string;
  fechaHasta: string;
  horaInicio: string | null;
  horaFin: string | null;
  motivo: string;
}

// ----------------------------------- FASE 6.4: resumen de planificación

export type TipoAlertaPlanificacion =
  | 'OT_SIN_ASIGNAR'
  | 'OPERARIO_NO_APTO'
  | 'SOLAPAMIENTO'
  | 'FUERA_DISPONIBILIDAD'
  | 'SOBRECARGA'
  | 'BLOQUEO'
  | 'OT_VENCIDA'
  | 'COBERTURA_BAJA'
  /** FASE 6.4 FINAL GATE §1: sin patrón de disponibilidad configurado — nunca "0h conocidas". */
  | 'DISPONIBILIDAD_SIN_CONFIGURAR';

export type SeveridadAlertaPlanificacion = 'INFO' | 'AVISO' | 'ALTA';

export interface AlertaPlanificacion {
  tipo: TipoAlertaPlanificacion;
  severidad: SeveridadAlertaPlanificacion;
  idCentro: string;
  idOt?: string;
  /** FASE 6.7-B1: OT implicadas cuando la alerta es de varias (SOLAPAMIENTO). Contrato de asociación — nunca `mensaje`. */
  idsOt?: string[];
  codigoOt?: string;
  idOperario?: string;
  operarioNombre?: string;
  fecha?: string;
  mensaje: string;
  detalles?: string;
}

/** `SIN_CONFIGURAR` (FINAL GATE §1): no hay disponibilidad configurada — nunca se confunde con `SIN_CARGA` (que SÍ conoce la capacidad). */
export type EtiquetaCarga = 'SIN_CARGA' | 'CARGA_PARCIAL' | 'CAPACIDAD_COMPLETA' | 'SOBRECARGA_ESTIMADA' | 'SIN_CONFIGURAR';

export interface MetricasDia {
  fecha: string;
  numeroOts: number;
  numeroOtsActivas: number;
  minutosCarga: number;
  minutosCapacidad: number;
  /** Operarios habilitados sin disponibilidad configurada ese día — no cuentan en `minutosCapacidad`. */
  operariosSinConfigurar: number;
  otsSinAsignar: number;
  operariosDistintos: number;
  otsConAlertas: number;
  numeroAlertas: number;
}

export interface CargaOperarioResumen {
  idOperario: string;
  operarioCodigo: string;
  operarioNombre: string;
  /** null = sin ningún patrón de disponibilidad configurado en el rango — capacidad desconocida, nunca cero. */
  minutosCapacidad: number | null;
  minutosCarga: number;
  numeroOts: number;
  porcentajeCarga: number | null;
  etiqueta: EtiquetaCarga;
  numeroAlertas: number;
}

export interface CoberturaPlan {
  idPlan: string;
  codigoPlan: string;
  fechaMaximaMaterializada: string | null;
  diasCobertura: number | null;
  baja: boolean;
}

export interface ResumenPlanificacion {
  idCentro: string;
  desde: string;
  hasta: string;
  totales: {
    minutosCarga: number;
    minutosCapacidad: number;
    numeroOts: number;
    otsSinAsignar: number;
    operariosAsignados: number;
    numeroAlertas: number;
  };
  dias: MetricasDia[];
  operarios: CargaOperarioResumen[];
  alertas: AlertaPlanificacion[];
  cobertura: CoberturaPlan[];
}

// ------------------------------------------------------------- planner (FASE 6.6)

export interface PlannerLineaProyectada {
  idPlanLinea: string;
  referenciaTarea: string;
  idUbicacion: string;
  idTipo: string;
}

/** FASE 6.6-B: quién exactamente — `operariosAsignados` (abajo) sigue siendo sólo el contador de 6.6-A. */
export interface OperarioAsignadoEvento {
  idOperario: string;
  codigo: string;
  nombre: string;
  alcance: string;
}

/**
 * FASE 6.7-A §B: fuente única de qué ofrece el Planner sobre esta OT — el
 * frontend nunca reimplementa esta condición, sólo lee `acciones` y
 * deshabilita controles en consecuencia. El backend vuelve a comprobar
 * todo dentro de cada mutación real; esto sólo gobierna qué se OFRECE.
 */
export interface AccionesPlannerOt {
  puedeReprogramar: boolean;
  puedeRedimensionar: boolean;
  puedeAsignar: boolean;
  puedeDesasignar: boolean;
  motivosBloqueo: string[];
}

export interface PlannerEventoMaterializado {
  tipo: 'OT';
  idOt: string;
  codigo: string;
  estado: string;
  origen: string;
  fecha: string;
  inicioLocal: string | null;
  inicioProgramadoDesconocido: boolean;
  duracionPlanificadaMin: number;
  /** FASE 6.7-A: SUM(ot_lineas.duracion_est_min_snapshot) — para calcular el nuevo ajuste al redimensionar. */
  duracionBaseMin: number;
  idPlan: string | null;
  codigoPlan: string | null;
  idSala: string;
  codigoSala: string;
  /**
   * Checkpoint post-F14.3: nombre de la sala, tareas DISTINTAS de la OT (fotografía de sus líneas, en su orden, como
   * mucho 20) y las ubicaciones de esas tareas. Inicio identifica la OT con ellas y busca en ellas. Opcionales para
   * tolerar respuestas anteriores.
   */
  descripcionSala?: string;
  tareas?: string[];
  ubicacionesTareas?: string[];
  operariosAsignados: number;
  operarios: OperarioAsignadoEvento[];
  alertas: AlertaPlanificacion[];
  acciones: AccionesPlannerOt;
  /**
   * F10.1: avance = líneas ejecutadas (CONFIRMADA/VERIFICADA/NO_OK/NO_APLICA) / totales. NO_OK y NC
   * abiertas son atención de calidad, aparte. Opcionales sólo para tolerar respuestas anteriores a 10.1.
   */
  avance?: { lineasTotales: number; lineasEjecutadas: number; lineasNoOk: number };
  ncAbiertas?: number;
}

export interface PlannerEventoProyectado {
  tipo: 'PROYECCION';
  identidad: string;
  fecha: string;
  hora: string;
  duracionEstimadaMin: number;
  idPlan: string;
  codigoPlan: string;
  idSala: string;
  codigoSala: string;
  lineas: PlannerLineaProyectada[];
}

export type PlannerEvento = PlannerEventoMaterializado | PlannerEventoProyectado;

export interface ResultadoPlanner {
  idCentro: string;
  desde: string;
  hasta: string;
  eventos: PlannerEvento[];
  dias: MetricasDia[];
  alertas: AlertaPlanificacion[];
  operarios: CargaOperarioResumen[];
  cobertura: CoberturaPlan[];
  totales: ResumenPlanificacion['totales'];
  /** Pre-F14.2: panel operativo, calculado sobre EXACTAMENTE las OT de `eventos`. */
  panel?: PanelPlanner;
}

/** Réplica de `LineaNoOkPanel` (backend/src/planificacion/tipos.ts). */
export interface LineaNoOkPanel {
  idOt: string;
  codigoOt: string;
  estadoOt: string;
  idLinea: string;
  orden: number;
  fecha: string;
  cliente: string;
  centro: string;
  ubicacion: string;
  tarea: string;
  motivo: string | null;
  resolucion: { idNc: string; codigoNc: string; estadoNc: string } | null;
}

/** Réplica de `PanelPlanner`: `tareas.noOk` es siempre `lineasNoOk.length`. */
/** PC-v2.1-A — situaciones EXCLUYENTES de las OT (suman `total`, sin anuladas) e indicadores transversales. */
export interface ResumenOts {
  total: number;
  previstas: number;
  enCurso: number;
  finalizadasSinVerificar: number;
  verificadas: number;
  noRealizadas: number;
  conIncidencia: number;
  fueraDePlazo: number;
  anuladas: number;
}

export interface PanelPlanner {
  otIncluidas: number;
  ots: ResumenOts;
  /** `completadas` (incluye NO_APLICA) se conserva por compatibilidad; ejecutadas + noAplica + pendientes = total. */
  tareas: { total: number; completadas: number; verificadas: number; noOk: number; ejecutadas: number; noAplica: number; pendientes: number };
  lineasNoOk: LineaNoOkPanel[];
}

/**
 * FASE 6.7-A §H: un cambio HIPOTÉTICO todavía no guardado, para pedir al
 * backend qué avisos generaría — nunca autoriza nada, el backend revalida
 * todo de nuevo al aplicar de verdad.
 */
export interface CambioHipoteticoOt {
  idOt: string;
  /** ISO 8601 con offset. Omitir = no mover. */
  inicioProgramadoEn?: string;
  /** `undefined`/`null` = no tocar. */
  ajusteDuracionPlanificacionMin?: number | null;
  operariosAnadir?: string[];
  operariosQuitar?: string[];
}

/** FASE 6.7 (UAT): nivel de un aviso de impacto — BLOQUEANTE (no elegible), AVISO o INFORMACION (p.ej. sin disponibilidad configurada). */
export type NivelAviso = 'BLOQUEANTE' | 'AVISO' | 'INFORMACION';

/** Un aviso del impacto de un cambio, ya deduplicado por el backend (p.ej. un solapamiento = 1 aviso con sus dos OT y sus operarios). */
export interface AvisoImpacto {
  tipo: string;
  nivel: NivelAviso;
  mensaje: string;
  idsOt: string[];
  operarios: { idOperario: string; nombre: string }[];
  fecha?: string;
}

/** Impacto del cambio (directo + relacionado) frente al contexto general del periodo (sólo el número). */
export interface ImpactoCambio {
  directos: AvisoImpacto[];
  relacionados: AvisoImpacto[];
  numeroGlobales: number;
}

export interface ResultadoPreviewCambio {
  /** Todas las alertas del periodo en el mundo hipotético — contexto, NO el impacto del cambio. */
  alertas: AlertaPlanificacion[];
  impacto: ImpactoCambio;
}

// ------------------------------------------------------------- bulk de planificación (FASE 6.7-B1)

/** Desplazamiento en el calendario LOCAL del centro — nunca se calcula aquí, lo aplica el backend. */
export interface DeltaPlanificacion {
  dias: number;
  horas: number;
  minutos: number;
}

export type OperacionBulkPlanificacion = 'DESPLAZAR' | 'ANADIR_OPERARIO' | 'QUITAR_OPERARIO';
export type ResultadoPrevistoBulk = 'CAMBIAR' | 'SIN_CAMBIO' | 'NO_ELEGIBLE';

export interface FilaPreviewBulk {
  idOt: string;
  codigo: string;
  estado: string;
  resultado: ResultadoPrevistoBulk;
  motivos: string[];
  cambioPropuesto: string;
  /** ISO UTC — se devuelve tal cual al confirmar como control de concurrencia. */
  inicioActual: string | null;
  inicioActualLocal: string | null;
  inicioPropuesto: string | null;
  inicioPropuestoLocal: string | null;
  duracionPlanificadaMin: number;
  operariosActuales: OperarioAsignadoEvento[];
  apto: boolean | null;
  alertas: AlertaPlanificacion[];
}

export interface ResultadoPreviewBulk {
  operacion: OperacionBulkPlanificacion;
  total: number;
  elegibles: number;
  noElegibles: number;
  sinCambio: number;
  aCambiar: number;
  numeroAvisos: number;
  conflictos: AlertaPlanificacion[];
  puedeConfirmar: boolean;
  filas: FilaPreviewBulk[];
}

export interface ResultadoBulkReprogramar {
  total: number;
  reprogramadas: number;
}

export interface ResultadoBulkAsignaciones {
  total: number;
  cambiadas: number;
  sinCambio: number;
}

// ------------------------------------------------------------- copiar semana (FASE 6.7-B2)

export type TipoOrigenCopia = 'MANUAL' | 'PLAN_MATERIALIZADA' | 'PROYECCION';
export type ResultadoCopia = 'CREAR' | 'YA_EXISTE_OMITIR' | 'CONFLICTO' | 'NO_COPIABLE';
export type OperacionCopia = 'CREAR_MANUAL' | 'MATERIALIZAR_PLAN' | 'NINGUNA';

/** Clasificación hecha por el BACKEND a partir de lo que hay de verdad en la semana origen — nunca por el frontend. */
export interface FilaCopiaSemana {
  clave: string;
  origen: {
    tipo: TipoOrigenCopia;
    basadaEnPlan: boolean;
    idOt: string | null;
    codigo: string | null;
    estado: string | null;
    codigoPlan: string | null;
    codigoSala: string;
    fecha: string;
    hora: string | null;
  };
  destino: {
    fecha: string;
    hora: string | null;
    /** Se devuelve tal cual al aplicar: sólo sirve para rechazar si la ocurrencia destino cambió. */
    identidadPlan: string | null;
    codigoExistente: string | null;
  };
  resultado: ResultadoCopia;
  operacion: OperacionCopia;
  motivo: string | null;
  warnings: string[];
  alertas: AlertaPlanificacion[];
  asignaciones: { nombre: string; codigo: string; procedencia: 'DEFECTO_PLAN' | 'COPIADA' }[];
}

export interface ResultadoPreviewCopiaSemana {
  semanaOrigen: string;
  semanaDestino: string;
  copiarAsignaciones: boolean;
  filas: FilaCopiaSemana[];
  resumen: { total: number; crear: number; yaExiste: number; conflicto: number; noCopiable: number };
  conflictos: AlertaPlanificacion[];
}

export interface ResultadoAplicarCopiaSemana {
  idOperacion: string;
  semanaDestino: string;
  manualesCreadas: number;
  ocurrenciasMaterializadas: number;
  asignacionesCopiadas: number;
}

export interface Plan {
  id: string;
  codigo: string;
  descripcion: string;
  version: number;
  estado: string;
  /**
   * PLAN-UX-01-A/0037: NULL sólo en BORRADOR — un borrador no tiene
   * vigencia hasta que se publica. Nunca lo trates como un dato ausente:
   * `fecha(null)` ya cae en «—», y el editor lo explica como "se
   * determinará al publicar".
   */
  vigenteDesde: string | null;
  vigenteHasta: string | null;
  lineas: number;
  fuente: string | null;
  /** NULL permitido sólo en BORRADOR. */
  horaProgramada: string | null;
}

/** F10.4 — una versión en el listado global `GET /api/planes` (con su centro y cliente). */
export interface PlanGlobal extends Plan {
  idCentro: string;
  centroCodigo: string;
  centroDescripcion: string;
  idCliente: string;
  clienteRazonSocial: string;
}

/** Devuelto por GET /api/planes/:id (a diferencia del listado, trae de qué centro/cliente es). */
export interface PlanDetalle extends Plan {
  idCentro: string;
  idCliente: string;
  centroCodigo: string;
  clienteRazonSocial: string;
  /** PLAN-UX-DEBT-04: toda la familia (mismo código+centro), no sólo esta versión. */
  otsGeneradas: number;
}

/** `PATCH /api/planes/:id` — sólo mientras es BORRADOR. */
export interface CambiosPlan {
  descripcion?: string;
  horaProgramada?: string | null;
}

export interface LineaPlan {
  id: string;
  referenciaTarea: string;
  idUbicacion: string;
  ubicacion: string;
  ubicacionRuta: string | null;
  descripcionTarea: string | null;
  idTipo: string;
  tipo: string;
  frecuencia: string;
  rrule: string | null;
  fechaInicio: string | null;
  politicaFestivo: string;
  fueraContrato: boolean;
  duracionEstMin: number | null;
}

/** ASSIGN-DB-01: default de asignación de una versión de Plan. GET/PUT /api/planes/:id/operarios. */
export interface DefaultAsignacionPlan {
  idPlanOperario: string;
  idOperario: string;
  operarioCodigo: string;
  operarioNombre: string;
  alcance: 'TODA_OT' | 'LINEAS';
  /** Sólo relevante si alcance='LINEAS'. Ids de LineaPlan de ESTE plan. */
  idsPlanLineas: string[];
}

/** Estado deseado que manda el PUT — sustituye siempre el conjunto completo. */
export interface DatosDefaultPlan {
  idOperario: string;
  alcance: 'TODA_OT' | 'LINEAS';
  idsPlanLineas: string[];
}

/** `POST /api/planes/:id/lineas` — la referencia la genera el servidor (MAN-N). */
export interface DatosLinea {
  idUbicacion: string;
  idTipo: string;
  descripcionTarea: string;
  frecuenciaEtiqueta: string;
  rrule: string | null;
  fechaInicioRecurrencia: string | null;
  duracionEstMin: number | null;
  politicaFestivo: string;
  fueraContrato: boolean;
}

/**
 * `PATCH /api/planes/:id/lineas/:id` — parcial. Sólo alcanza a líneas de un
 * BORRADOR (el propio servicio lo exige): una OT nunca sale de una línea de
 * borrador, así que cambiar aquí ubicación o tipo no rompe ningún historial.
 */
export type CambiosLinea = Partial<
  Pick<
    DatosLinea,
    'idUbicacion' | 'idTipo' | 'descripcionTarea' | 'frecuenciaEtiqueta' | 'rrule' | 'fechaInicioRecurrencia' | 'duracionEstMin' | 'politicaFestivo' | 'fueraContrato'
  >
>;

/** FASE 6.5. A/B/C/D — ver ModalPublicar (editor-plan.tsx) para el texto en lenguaje humano de cada una. */
export type EstrategiaPublicacion = 'A' | 'B' | 'C' | 'D';
export type DecisionOtPublicacion = 'MANTENER' | 'APLICAR_COMPATIBLE' | 'ANULAR_REEMITIR';

export interface ResumenEstrategiaOtPlan {
  idOt: string;
  codigoOt: string;
  clasificacion: 'SIN_CAMBIO' | 'COMPATIBLE' | 'ESTRUCTURAL';
  protegida: boolean;
  decision: DecisionOtPublicacion;
  resultado: 'APLICADA' | 'RECHAZADA';
  detalle: string | null;
  idOtNueva: string | null;
  codigoOtNueva: string | null;
}

export interface ResultadoPublicarPlan {
  idPlan: string;
  codigo: string;
  version: number;
  vigenteDesde: string;
  sustituyoA: string | null;
  yaEstaba: boolean;
  /** FASE 6.5. 'A' si no hubo estrategia real que decidir (sin impacto, o publicación simple). */
  estrategia: EstrategiaPublicacion;
  resumenOts: ResumenEstrategiaOtPlan[];
  idPublicacion: string | null;
}

export interface OcurrenciaPreviewPlan {
  idLinea: string;
  referenciaTarea: string;
  ubicacionCodigo: string;
  descripcionTarea: string;
  fecha: string;
  fechaHora: string | null;
  desplazada: boolean;
}

export interface AvisoPreviewPlan {
  codigo: 'SIN_DIA_OPERATIVO' | 'EJECUTA_EN_DIA_NO_OPERATIVO' | 'RRULE_INVALIDA';
  idLinea: string;
  referenciaTarea: string;
  mensaje: string;
}

export interface PreviewProgramacionPlan {
  idPlan: string;
  horaProgramada: string | null;
  desde: string;
  hasta: string;
  totalLineas: number;
  lineasSegunNecesidad: number;
  lineasFueraContrato: number;
  proximasOcurrencias: OcurrenciaPreviewPlan[];
  totalOcurrenciasEnHorizonte: number;
  avisos: AvisoPreviewPlan[];
}

/** `GET /api/planes/:id/impacto-hipotetico` — PLAN-UX-01-C, punto 11: informe de impacto ANTES de publicar. */
export type ImpactoHipotetico =
  | { sinVersionAnterior: true }
  | {
      sinVersionAnterior: false;
      idPlanAnterior: string;
      versionAnterior: number;
      versionNueva: number;
      tareasAnadidas: number;
      tareasModificadas: number;
      tareasRetiradas: number;
      otsEvaluadas: number;
      otsImpactadas: number;
      otsImpactadasCerradas: number;
      otsImpactadasAnuladas: number;
      otsNuevasRequeridas: number;
      conflictosGenerador: number;
      avisos: { codigo: string; mensaje: string; referenciaTarea?: string; fecha?: string }[];
      /** FASE 6.5. */
      clasificacionGlobal: 'SIN_IMPACTO' | 'COMPATIBLE' | 'ESTRUCTURAL';
      otsProtegidas: number;
      otsElegiblesB: number;
      otsElegiblesC: number;
      estrategiasDisponibles: EstrategiaPublicacion[];
      ots: {
        idOt: string;
        codigoOt: string;
        nivel: string;
        clasificacion: 'SIN_CAMBIO' | 'COMPATIBLE' | 'ESTRUCTURAL';
        protegida: boolean;
        /** F10.4: por qué no puede sustituirse (mismas reglas de siempre); opcional en respuestas anteriores. */
        motivosProteccion?: string[];
      }[];
    };

/** Plantilla oficial de planes (estabilización post-F13): hojas «Plan» + «Ubicaciones». */
export const PLANTILLA_PLAN = 'NeatOS_Plan_Limpieza_v2.xlsx';

/** Plantilla v2 — una fila de la hoja «Ubicaciones» y qué se hará con ella. */
export interface UbicacionImportacion {
  numeroFila: number;
  codigo: string;
  descripcion: string | null;
  tipo: string | null;
  codigoPadre: string | null;
  accion: 'REUTILIZAR' | 'CREAR' | 'CONFLICTO' | 'ERROR';
  creadaAlConfirmar: boolean;
  errores: { codigo: string; campo: string | null; detalle: string }[];
  avisos: string[];
}

export interface Importacion {
  id: string;
  codigoPlan: string;
  estado: string;
  creadaEn: string;
  idPlanBase: string | null;
  idPlanGenerado: string | null;
  errorCodigo: string | null;
  /** El motivo de un lote FALLIDA, legible (otra plantilla, versión desconocida…). */
  errorDetalle?: string | null;
  filas: number;
  filasConError: number;
  /** Plantilla v2: la hoja «Ubicaciones» (vacía con la v1). */
  ubicaciones?: UbicacionImportacion[];
  errores?: {
    fila: number;
    referencia: string | null;
    ubicacion: string | null;
    estado: string;
    codigo: string | null;
    detalle: string | null;
  }[];
}

/** FUNC-OT-01 (UAT FASE 4.3): una línea del formulario de OT manual. */
export interface LineaOtManualPayload {
  idUbicacion: string;
  idTipo: string;
  observaciones?: string | null;
  /** PLAN-UX-01-C: tarea del plan de la que esta línea dice venir. El servidor decide si de verdad corresponde. */
  idPlanLinea?: string | null;
}

/** Cuerpo de `POST /api/ots` — OT manual/extraordinaria, origen PUNTUAL. */
export interface DatosOtManual {
  idCentro: string;
  idSala: string;
  fecha: string;
  hora: string;
  observaciones: string | null;
  lineas: LineaOtManualPayload[];
  /** PLAN-UX-01-C: versión de plan usada como base, si se eligió "Basar en plan". */
  idPlan?: string | null;
}

export interface OtManualCreada {
  id: string;
  codigo: string;
  idCliente: string;
  idCentro: string;
  idSala: string;
  origen: 'PUNTUAL';
  fechaPlan: string;
  inicioProgramadoEn: string;
  lineas: number;
  idPlan: string | null;
  lineasDePlan: number;
}

/** Una tarea del plan, ya resuelta para una fecha y sala concretas — `GET .../tareas-para-fecha`. */
export interface TareaPropuestaOt {
  idPlanLinea: string;
  referenciaTarea: string;
  idUbicacion: string;
  codigoUbicacion: string;
  rutaUbicacion: string | null;
  idTipo: string;
  codigoTipo: string;
  descripcionTipo: string;
  descripcionTarea: string | null;
  duracionEstMin: number | null;
  fueraContrato: boolean;
  segunNecesidad: boolean;
  fechaOriginal: string | null;
  desplazada: boolean;
  yaCubierta: boolean;
}

export interface PropuestaTareasOt {
  idPlan: string;
  codigoPlan: string;
  version: number;
  vigenteDesde: string;
  vigenteHasta: string | null;
  fecha: string;
  tareas: TareaPropuestaOt[];
}

export interface Ot {
  id: string;
  codigo: string;
  fechaPlan: string;
  fechaLimite: string;
  estado: string;
  origen: string;
  salaCodigo: string;
  planCodigo: string | null;
  planVersion: number | null;
  emision: number;
  lineas: number;
  cerrada: boolean;
  sustituida: boolean;
}

export interface PaginaOt {
  filas: Ot[];
  total: number;
  pagina: number;
  porPagina: number;
  salas: { id: string; codigo: string }[];
}

/** Fila del listado GLOBAL de OT (FASE 3.3-A): sin sala, con cliente/centro. */
export interface OtGlobal {
  id: string;
  codigo: string;
  fechaPlan: string;
  fechaLimite: string;
  estado: string;
  origen: string;
  clienteRazonSocial: string;
  centroCodigo: string;
  centroDescripcion: string;
  planCodigo: string | null;
  planVersion: number | null;
  emision: number;
  lineas: number;
  cerrada: boolean;
  sustituida: boolean;
}

export interface PaginaOtGlobal {
  filas: OtGlobal[];
  total: number;
  pagina: number;
  porPagina: number;
}

/** Un operario, tal y como aparece en `asignados`/`ejecutoresReales` de una línea (FASE 4.2-A/4.3-A). */
export interface OperarioDeLinea {
  idOperario: string;
  codigo: string;
  /** F10.1: nombre legible (la UI no muestra el código interno). */
  nombre: string;
}

/**
 * El hecho registrado más reciente de una línea (0030): el que explica su
 * `estado` actual. `null` en una línea PENDIENTE, que por definición no
 * tiene ningún evento todavía.
 *
 * SIN CAMPO `tipo` A PROPÓSITO: `ot_lineas.estado` YA ES el tipo de este
 * mismo evento — 0030 escribe los dos en el mismo INSERT/UPDATE, así que
 * duplicarlo aquí sería una segunda fuente del mismo dato. Para saber qué
 * clase de "última acción" fue (CONFIRMADA/VERIFICADA/NO_OK/NO_APLICA), la
 * UI lee `linea.estado`, nunca un campo separado dentro de este objeto.
 */
export interface EventoRelevanteLinea {
  fechaEfectiva: string;
  motivo: string | null;
  observacion: string | null;
}

/** Quien registró un hecho (CONFIRMADA/VERIFICADA) — el actor autenticado. */
export interface ActorLinea {
  idUsuario: string;
  nombre: string;
}

/** Hecho histórico de la última confirmación (FASE 4.3, BUG 1): CONFIRMADO POR ≠ VERIFICADO POR. */
export interface ConfirmacionLinea {
  fechaEfectiva: string;
  fechaRegistro: string;
  actor: ActorLinea;
  observacion: string | null;
  ejecutoresReales: OperarioDeLinea[];
}

/** Hecho histórico de la última verificación — su propio actor, nunca el de la confirmación. */
export interface VerificacionLinea {
  fechaEfectiva: string;
  fechaRegistro: string;
  actor: ActorLinea;
  observacion: string | null;
  /** PC-v2.1 (0099): INTERNO o CLIENTE (desde el portal); null en las anteriores a 0099. */
  origen: 'INTERNO' | 'CLIENTE' | null;
}

/** Hint de UX (FASE 4.2-A.3): qué admitiría la máquina ahora mismo. Nunca autoridad — ver ejecucion/acciones-permitidas.ts. */
export interface AccionesPermitidasLinea {
  confirmar: boolean;
  verificar: boolean;
  noOk: boolean;
  noAplica: boolean;
}

export interface LineaOt {
  id: string;
  orden: number;
  referenciaTarea: string | null;
  ubicacionCodigo: string;
  ubicacionRuta: string;
  descripcionTarea: string | null;
  tipoCodigo: string;
  tipoDescripcion: string;
  fueraContrato: boolean | null;
  duracionEstMin: number | null;
  estado: string;
  observaciones: string | null;
  ocurrencias: string[];
  requisitos: { codigo: string; nivelMinimo: number; obligatorio: boolean }[];
  reemiteA: string | null;
  /** Asignación PLANIFICADA (0010) — nunca el ejecutor real. */
  asignados: OperarioDeLinea[];
  /**
   * Ejecutores reales (0030) de la ÚLTIMA CONFIRMACIÓN — no del evento más
   * reciente de cualquier tipo. Sobrevive a una verificación posterior
   * (BUG 1, FASE 4.3). Vacío sólo si la línea nunca se ha confirmado.
   */
  ejecutoresReales: OperarioDeLinea[];
  eventoRelevante: EventoRelevanteLinea | null;
  /** Hecho de la última confirmación, con su propio actor — null si nunca se confirmó. */
  confirmacion: ConfirmacionLinea | null;
  /** Hecho de la última verificación, con su propio actor — null si nunca se verificó. */
  verificacion: VerificacionLinea | null;
  /** F10.0 (0077): NC no anuladas que cubren la línea — condición para «Rehacer» una línea No OK. */
  ncAsociadas: number;
  /** F10.2: esas NC con código y estado (la línea No OK enseña cuál es). Opcional: respuestas anteriores a 10.2. */
  ncs?: { id: string; codigo: string; estado: string }[];
  accionesPermitidas: AccionesPermitidasLinea;
}

/** Cuerpo común a las cuatro acciones de ejecución (FASE 4.2-A). */
export interface DatosAccionLinea {
  fechaEfectiva: string;
  observacion?: string | null;
  idOperacionCliente: string;
}
export interface DatosConfirmarLinea extends DatosAccionLinea {
  ejecutoresReales: string[];
}
export interface DatosMotivoLinea extends DatosAccionLinea {
  motivo: string;
}

/** Respuesta de las cuatro acciones unitarias de `POST /api/ot-lineas/:id/*`. */
export interface ResultadoAccionLinea {
  idEvento: string;
  idLinea: string;
  estadoAnterior: string;
  estadoNuevo: string;
  fechaEfectiva: string;
  fechaRegistro: string;
  /** Estado EFECTIVO de la OT en el momento de ESTA respuesta — ver ejecucion/tipos.ts. */
  otEstado: string;
  yaEstaba: boolean;
}

/**
 * Ítem de un lote masivo (FASE 4.2-C): autocontenido, línea a línea — el
 * backend nunca reconstruye nada a partir de un valor "común"; ese
 * concepto es exclusivamente de esta UI. `idOperacionCliente` es
 * OBLIGATORIA aquí, a diferencia del contrato unitario.
 */
export interface ItemConfirmarMasivo {
  idLinea: string;
  ejecutoresReales: string[];
  fechaEfectiva: string;
  observacion?: string | null;
  idOperacionCliente: string;
}
export interface ItemVerificarMasivo {
  idLinea: string;
  fechaEfectiva: string;
  observacion?: string | null;
  idOperacionCliente: string;
}

/** Una línea rechazada de un lote — nunca aborta a las demás. */
export interface RechazoLinea {
  idLinea: string;
  codigo: string;
  mensaje: string;
}

/** Respuesta de `POST /api/ot-lineas/{confirmar,verificar}-masivo`. */
export interface ResultadoAccionMasiva {
  total: number;
  procesadas: ResultadoAccionLinea[];
  rechazadas: RechazoLinea[];
}

export interface OtDetalle {
  id: string;
  codigo: string;
  idCentro: string;
  idCliente: string;
  clienteRazonSocial: string;
  /** Logo del cliente (L99 punto 4), ya resuelto — `null` sin logo. */
  clienteLogoUrl: string | null;
  centroCodigo: string;
  centroDescripcion: string;
  salaCodigo: string;
  salaDescripcion: string;
  fechaPlan: string;
  fechaLimite: string;
  estado: string;
  origen: string;
  emision: number;
  cerrada: boolean;
  planCodigo: string | null;
  planVersion: number | null;
  observaciones: string | null;
  /** Instante ISO del inicio previsto (null = legado sin hora). */
  inicioProgramadoEn?: string | null;
  lineas: LineaOt[];
  cadena: { id: string; codigo: string; emision: number; estado: string; motivo: string | null }[];
  /**
   * F10.2: lo que falta para verificar/cerrar, ya redactado por el servidor (condiciones existentes del
   * motor; el frontend no las recalcula). Vacío mientras la ejecución no ha terminado.
   */
  pendienteParaCerrar?: string[];
}

/**
 * No conformidad — resumen (GET /api/ots/:idOt/no-conformidades, GET
 * /api/no-conformidades). `alcance` es un campo calculado por el backend
 * ('LINEAS' cuando hay filas asociadas, 'TODA_OT' si no): no existe una
 * columna física de alcance (FASE 4.2-B), y el frontend nunca reinterpreta
 * la ausencia de líneas por su cuenta.
 */
export type AlcanceNc = 'TODA_OT' | 'LINEAS';

export interface NcFila {
  id: string;
  codigo: string;
  /** null = NC general de centro (FASE 7.1, 0055) — sólo nace al escalar una incidencia de centro. */
  idOt: string | null;
  otCodigo: string | null;
  /** F11.3: null = NC del cliente pendiente de clasificar (la gravedad oficial la fija la empresa). */
  gravedad: string | null;
  origen: string;
  estado: string;
  descripcion: string;
  creadoEn: string;
  creadoPorNombre: string | null;
  alcance: AlcanceNc;
  /** FASE 7.2 — seguimiento. */
  responsableNombre?: string | null;
  fechaObjetivo?: string | null;
  vencida?: boolean;
  /**
   * F11.1 — contexto para entender la NC sin abrirla. Ubicación: la de la NC o
   * la sala de su OT. `diasAbierta`: días desde la apertura (hoy local del
   * centro), null si ya no está activa.
   */
  idCentro?: string;
  centroNombre?: string;
  ubicacionNombre?: string | null;
  visibleCliente?: boolean;
  lineasCodigos?: string[];
  incidenciaOrigen?: string | null;
  fechaCierre?: string | null;
  diasAbierta?: number | null;
  /** F11.3 — lo que percibe el cliente (no es la gravedad oficial). */
  impactoCliente?: string | null;
  urgenciaCliente?: string | null;
  /** F11.3 — última actividad del cliente y si la NC espera respuesta de la empresa. */
  ultimaActividadCliente?: string | null;
  esperaRespuesta?: boolean;
}

export interface NcLinea {
  id: string;
  ubicacionCodigo: string;
  ubicacionRuta: string;
  tipoCodigo: string;
  estado: string;
}

export interface NcHistorialFila {
  id: string;
  estadoAnterior: string | null;
  estadoNuevo: string;
  actorNombre: string;
  /** F11.3 */
  porCliente?: boolean;
  motivo: string | null;
  ocurridoEn: string;
  registradoEn: string;
}

/** Ficha completa de una NC (GET /api/no-conformidades/:id) — sólo se pide al expandir. */
export interface NcDetalle extends NcFila {
  idCliente: string;
  idCentro: string;
  idResponsable: string | null;
  responsableNombre: string | null;
  accionCorrectiva: string | null;
  fechaCierre: string | null;
  lineas: NcLinea[];
  historial: NcHistorialFila[];
  /** F11.3: la NC está cerrada por el cliente — quién, cuándo y con qué motivo. */
  cierreCliente?: { autor: string; ocurridoEn: string; motivo: string | null } | null;
}

export type GravedadNc = 'LEVE' | 'GRAVE' | 'CRITICA';
/** El cuerpo nunca puede pedir 'CLIENTE': el servidor lo deriva siempre de la identidad. */
export type OrigenNcSolicitado = 'INTERNA' | 'AUDITORIA';

export interface DatosCrearNc {
  idOt: string;
  /** Omitido o [] = afecta a toda la OT. */
  idsLineas?: string[];
  descripcion: string;
  gravedad: GravedadNc;
  origen?: OrigenNcSolicitado;
  idOperacionCliente: string;
}

/** Cuerpo común a iniciar-tratamiento/cerrar/anular — cada acción usa el campo que le corresponde. */
export interface DatosTransicionNc {
  motivo?: string | null;
  accionCorrectiva?: string;
  idOperacionCliente: string;
}

export interface ResultadoCrearNc {
  idHistorial: string;
  idNc: string;
  codigo: string;
  idOt: string;
  estado: string;
  origen: string;
  ocurridoEn: string;
  registradoEn: string;
  yaEstaba: boolean;
}

export interface ResultadoTransicionNc {
  idHistorial: string;
  idNc: string;
  estadoAnterior: string;
  estadoNuevo: string;
  idResponsable: string | null;
  accionCorrectiva: string | null;
  ocurridoEn: string;
  registradoEn: string;
  yaEstaba: boolean;
}

// ------------------------------------------------------- evidencias (4.4-D)

/** Mismo vocabulario que `backend/src/evidencias/tipos.ts` — nunca inventado aquí. */
/** FASE 7.1: INCIDENCIA y ACCION (acción correctiva de una NC). */
export type AmbitoEvidencia = 'OT' | 'LINEA' | 'NC' | 'INCIDENCIA' | 'ACCION';
export type ContextoOtLinea = 'GENERAL' | 'ANTES' | 'DESPUES' | 'RESULTADO';
export type ContextoNc = 'APERTURA_NC' | 'TRATAMIENTO_NC' | 'RESOLUCION_NC';
export type ContextoEvidencia = ContextoOtLinea | ContextoNc;
export type VisibilidadEvidencia = 'INTERNA' | 'CLIENTE';

export const CONTEXTOS_OT_LINEA: readonly ContextoOtLinea[] = ['GENERAL', 'ANTES', 'DESPUES', 'RESULTADO'];
export const CONTEXTOS_NC: readonly ContextoNc[] = ['APERTURA_NC', 'TRATAMIENTO_NC', 'RESOLUCION_NC'];

/**
 * Réplica exacta de `EvidenciaResumen` (backend/src/evidencias/tipos.ts) —
 * nunca storage_key/hash_sha256/rutas físicas: el backend no los expone y
 * este tipo no debe inventarlos. Sin nombre de quien subió/anuló (el
 * backend sólo da el UUID en `idUsuario`/`anuladoPor`) — la UI no lo
 * muestra por lo mismo (encargo 4.4-D, sección 6).
 */
export interface EvidenciaResumen {
  id: string;
  ambito: AmbitoEvidencia;
  contexto: ContextoEvidencia;
  visibilidad: VisibilidadEvidencia;
  estado: 'ACTIVA' | 'ANULADA';
  idUsuario: string;
  nombreOriginal: string;
  mime: string;
  tamanoBytes: number;
  descripcion: string | null;
  subidaEn: string;
  tomadaEn: string | null;
  anuladoPor: string | null;
  anuladoEn: string | null;
  motivoAnulacion: string | null;
}

// -------------------------------------------------------- documentos (4.4-E/F)

export type TipoDocumento = 'PROCEDIMIENTO' | 'INSTRUCCION' | 'FICHA_TECNICA' | 'SEGURIDAD' | 'OTRO';
export const TIPOS_DOCUMENTO: readonly TipoDocumento[] = [
  'PROCEDIMIENTO', 'INSTRUCCION', 'FICHA_TECNICA', 'SEGURIDAD', 'OTRO',
];
export const ETIQUETAS_TIPO_DOCUMENTO: Record<TipoDocumento, string> = {
  PROCEDIMIENTO: 'Procedimiento', INSTRUCCION: 'Instrucción', FICHA_TECNICA: 'Ficha técnica',
  SEGURIDAD: 'Seguridad', OTRO: 'Otro',
};
export type EstadoDocumentoMaestro = 'ACTIVO' | 'RETIRADO';
export type EstadoVersionDocumento = 'BORRADOR' | 'PUBLICADA' | 'RETIRADA';
export type AmbitoAplicabilidadDocumento = 'PLAN' | 'PLAN_LINEA';
export type AmbitoSnapshotDocumentoOt = 'OT' | 'LINEA';

/** Réplica de `FilaDocumentoMaestro` (backend/src/documentos/repositorio.ts). */
export interface DocumentoMaestro {
  id: string;
  idCliente: string | null;
  tipoDocumento: TipoDocumento;
  codigo: string;
  titulo: string;
  descripcion: string | null;
  visibleCliente: boolean;
  estado: EstadoDocumentoMaestro;
  creadoEn: string;
  /** Pre-F14.2 (0095): sólo para gestión interna — el portal no la recibe. */
  clientesAutorizados?: { id: string; razonSocial: string }[];
}

/** Réplica de `FilaVersion` — nunca storage_key/hash: el backend no los expone al listar/detallar. */
export interface VersionDocumento {
  id: string;
  idDocumentoMaestro: string;
  version: number;
  estado: EstadoVersionDocumento;
  vigenteDesde: string | null;
  vigenteHasta: string | null;
  nombreOriginal: string;
  mime: string;
  tamanoBytes: number;
  creadoEn: string;
  publicadoEn: string | null;
  retiradoEn: string | null;
}

export interface AplicabilidadDocumento {
  id: string;
  idDocumentoMaestro: string;
  codigo: string;
  titulo: string;
  tipoDocumento: TipoDocumento;
  ambito: AmbitoAplicabilidadDocumento;
  idPlanLinea: string | null;
}

/** Snapshot histórico congelado en una OT/línea (migración 0044) — nunca la versión "actual" del documento. */
export interface DocumentoAplicadoOt {
  id: string;
  ambito: AmbitoSnapshotDocumentoOt;
  tipoDocumento: TipoDocumento;
  titulo: string;
  codigo: string;
  version: number;
  nombreOriginal: string;
  mime: string;
  tamanoBytes: number;
  visibleCliente: boolean;
  resueltoEn: string;
}

/**
 * PORTAL CLIENTE — sus propias OT (PORTAL-DEBT-01). Forma DISTINTA de
 * `OtFilaGlobal`/`OtDetalle` (gestión interna): nunca operarios, eventos
 * ni cadena de reemisión — `GET /api/portal/ots*` no los expone.
 */
export interface OtPortal {
  id: string;
  codigo: string;
  estado: string;
  fechaPlan: string;
  fechaLimite: string;
  centroCodigo: string;
  centroDescripcion: string;
  /** Portal v2: qué trabajo es sin abrirla. */
  salaCodigo: string;
  salaDescripcion: string;
  /** Tareas distintas en el orden de la OT (como mucho 20). */
  tareas: string[];
  avance: { lineasTotales: number; lineasEjecutadas: number; lineasVerificadas: number; lineasNoOk: number };
  vencida: boolean;
  conIncidencia: boolean;
}

export interface OtPortalLinea {
  id: string;
  orden: number;
  ubicacionCodigo: string;
  ubicacionRuta: string;
  descripcionTarea: string | null;
  tipoCodigo: string;
  tipoDescripcion: string;
  estado: string;
  /** Portal v2 (0098): hechos reales — ejecutores de la confirmación y quien verificó; días AAAA-MM-DD. */
  realizadaPor: string | null;
  realizadaEn: string | null;
  verificadaPor: string | null;
  verificadaEn: string | null;
  noOkEn: string | null;
  /** PC-v2.1-B: el cliente puede verificarla (CONFIRMADA, OT operable, centro activo y permiso). */
  verificable: boolean;
}

/** Portal v2 — grupos de OT de `servicio-portal.ts` (filtro del listado, enlaces de Inicio). */
export type GrupoOtPortal = 'PENDIENTES' | 'VENCIDAS' | 'REALIZADAS' | 'VERIFICADAS' | 'NO_REALIZADAS' | 'CON_INCIDENCIA';

export interface CocientePortal { pct: number | null; n: number; total: number }

/** PC-v2.2-C1 — `GET /api/portal/evolucion`: las cifras de Inicio y Calidad, por semanas o meses. */
export interface TramoEvolucionPortal {
  /** Lunes o día 1 del tramo. */
  clave: string;
  /** Días del tramo dentro del periodo (recortado en los extremos: `parcial`). */
  desde: string;
  hasta: string;
  parcial: boolean;
  /** false: el tramo terminó antes del primer registro: «sin datos», nunca 0. */
  conDatos: boolean;
  ot: { previstas: number; realizadas: number; verificadas: number; noRealizadas: number; vencidas: number; cumplimiento: CocientePortal } | null;
  incidencias: { registradas: number; resueltas: number } | null;
  nc: { registradas: number; cerradas: number } | null;
}
export interface EvolucionPortal {
  periodo: { desde: string; hasta: string };
  idCentro: string | null;
  granularidad: 'semana' | 'mes';
  datosDesde: string | null;
  tramos: TramoEvolucionPortal[];
  porCentro: { idCentro: string; centro: string; incidencias: number; nc: number }[] | null;
}

/** PC-v2.2-C2.1 — G6 interno: como mucho 50 grupos con nombre; el resto, sumado. */
export interface RestoG6 { grupos: number; incidencias: number; nc: number }
/**
 * PC-v2.2-C2.1 — `GET /api/calidad/evolucion`: «Seguimiento del servicio» con el MISMO cálculo que el portal, sobre el
 * ámbito del usuario interno. Cada centro va con su cliente (los homónimos de clientes distintos nunca se juntan).
 */
export interface EvolucionInterna extends Omit<EvolucionPortal, 'porCentro'> {
  idCliente: string | null;
  porCentro: { filas: { idCentro: string; centro: string; idCliente: string; cliente: string; incidencias: number; nc: number }[]; resto: RestoG6 | null } | null;
  porCliente: { filas: { idCliente: string; cliente: string; centros: number; incidencias: number; nc: number }[]; resto: RestoG6 | null } | null;
}

export interface OtAtencionPortal {
  id: string; codigo: string; fecha: string; fechaLimite: string; estado: string; tarea: string | null; centro: string; tareasNoOk: number;
}

export interface ResumenServicioPortal {
  periodo: { desde: string; hasta: string };
  idCentro: string | null;
  ot: { previstas: number; realizadas: number; verificadas: number; pendientes: number; vencidas: number; noRealizadas: number; conIncidencia: number };
  tareas: { total: number; realizadas: number; verificadas: number; noOk: number };
  cumplimiento: CocientePortal;
  verificacion: CocientePortal;
  atencion: {
    vencidas: { total: number; ots: OtAtencionPortal[] };
    noOk: { total: number; ots: OtAtencionPortal[] };
    incidencias: { total: number; filas: { id: string; codigo: string; titulo: string; estado: string; creadaEn: string; centro: string }[] } | null;
    noConformidades: { total: number; filas: { id: string; codigo: string; titulo: string; estado: string; fechaApertura: string; centro: string }[] } | null;
  };
}

export interface EventoPlannerPortal {
  idOt: string;
  codigo: string;
  estado: string;
  fecha: string;
  inicioLocal: string | null;
  inicioProgramadoDesconocido: boolean;
  duracionMin: number;
  codigoSala: string;
  descripcionSala: string;
  tareas: string[];
  avance: { lineasTotales: number; lineasEjecutadas: number; lineasNoOk: number };
  vencida: boolean;
  conIncidencia: boolean;
}

export interface PlannerPortal {
  idCentro: string;
  centro: string;
  zonaHoraria: string;
  desde: string;
  hasta: string;
  eventos: EventoPlannerPortal[];
  panel: {
    otIncluidas: number; realizadas: number; verificadas: number; pendientes: number; vencidas: number; conIncidencia: number;
    ots: ResumenOts;
    tareas: { total: number; realizadas: number; verificadas: number; noOk: number; noAplica: number; pendientes: number };
  };
}

export interface OtPortalDetalle extends OtPortal {
  lineas: OtPortalLinea[];
}

export interface PaginaOtPortal {
  filas: OtPortal[];
  total: number;
  pagina: number;
  porPagina: number;
}

/**
 * PAQUETE OFFLINE (FASE 5.1) — `GET /api/offline/paquete`.
 *
 * Sólo lectura: la persistencia local vive en `offline/db.ts`, nunca aquí.
 */
export interface PaqueteOtOffline {
  id: string;
  codigo: string;
  idCentro: string;
  centroCodigo: string;
  centroDescripcion: string;
  salaCodigo: string;
  salaDescripcion: string;
  estado: string;
  inicioProgramadoEn: string;
  fechaLimite: string;
  observaciones: string | null;
  alcanceOperario: 'TODA_OT' | 'LINEAS' | null;
  /**
   * F14.2 — lo que «Mis trabajos» necesita sin conexión. Opcionales: un paquete guardado antes de F14.2 no los trae
   * (se sustituye en la siguiente descarga).
   */
  clienteNombre?: string;
  duracionMin?: number;
}

/** F14.2 — NC que cubre la línea (condición de REHACER). */
export interface NcAsociadaLinea {
  codigo: string;
  estado: string;
}

export interface PaqueteLineaOffline {
  id: string;
  idOt: string;
  orden: number;
  ubicacionCodigo: string;
  ubicacionRuta: string;
  descripcionTarea: string | null;
  tipoCodigo: string;
  tipoDescripcion: string;
  estado: string;
  observaciones: string | null;
  asignados: { idOperario: string; codigo: string; nombre?: string }[];
  revisionBase: string | null;
  //  F14.2 — opcionales por la misma razón que en `PaqueteOtOffline`.
  ubicacionDescripcion?: string;
  procedimiento?: { nombre: string; url: string | null } | null;
  duracionMin?: number | null;
  /** Material PREVISTO (sólo lectura): no es entregado ni consumido. */
  materialesPrevistos?: PrevistoOperario[];
  tieneNc?: boolean;
  ncAsociada?: NcAsociadaLinea | null;
}

export interface PaqueteDocumentoOffline {
  id: string;
  /** F14.3 — `PT`: documento del activo o del parte de mantenimiento (`idOt` = id del parte). */
  ambito: 'OT' | 'LINEA' | 'PT';
  idOt: string;
  idOtLinea: string | null;
  tipoDocumento: string;
  titulo: string;
  codigo: string;
  version: number;
  nombreOriginal: string;
  mime: string;
  tamanoBytes: number;
  hashSha256: string;
}

export interface PaqueteOffline {
  generadoEn: string;
  validoOperativamenteHasta: string;
  /** F14.2 — +72 h: desde aquí no se empieza trabajo nuevo con este paquete (`offline/caducidad.ts`). */
  bloqueaTrabajoNuevoDesde?: string;
  ventanaDesde: string;
  ventanaHasta: string;
  rol: 'ENCARGADO' | 'OPERARIO' | 'TECNICO';
  centros: string[];
  ots: PaqueteOtOffline[];
  lineas: PaqueteLineaOffline[];
  documentos: PaqueteDocumentoOffline[];
  /** F14.3 — sólo para el TÉCNICO de mantenimiento (`null` en los demás roles; ausente en paquetes anteriores). */
  mantenimiento?: PaqueteMantenimiento | null;
}

// ---------------------------------------------------------------- mantenimiento en campo (F14.3)

/** Réplica de `backend/src/mantenimiento/campo.ts` — lo que el técnico necesita para trabajar con y sin red. */
export interface ActivoCampo {
  id: string;
  codigo: string;
  nombre: string;
  estado: string;
  tipo: string;
  ubicacion: string | null;
  fabricante: string | null;
  modelo: string | null;
  numeroSerie: string | null;
  referenciaCliente: string | null;
  descripcion: string | null;
}

export type TipoNotaCampo = 'DIAGNOSTICO' | 'TRABAJO' | 'OBSERVACION';

export interface NotaCampo {
  id: string;
  idIntervencion: string;
  tipo: TipoNotaCampo;
  texto: string;
  ocurridoEn: string;
  registradoEn: string;
  autor: string;
}

export interface ConsumoCampo {
  id: string;
  estado: string;
  articulo: { id: string; codigo: string; descripcion: string };
  almacen: { id: string; codigo: string; descripcion: string };
  cantidad: string;
  unidad: string;
  lote: string | null;
  idIntervencion: string | null;
  registradoEn: string;
}

export interface ParteCampo {
  parte: ParteDetalle;
  activos: ActivoCampo[];
  plan: { codigo: string; nombre: string; tipoTrabajo: string; descripcion: string | null } | null;
  notas: NotaCampo[];
  consumos: ConsumoCampo[];
}

export interface DocumentoCampo {
  id: string;
  idPt: string;
  origen: 'ACTIVO' | 'PARTE';
  idActivo: string | null;
  titulo: string;
  nombreOriginal: string;
  mime: string;
  tamanoBytes: number;
  hashSha256: string;
}

export interface AlmacenCampo {
  id: string;
  codigo: string;
  descripcion: string;
  tipo: string;
  propio: boolean;
  idCentro: string | null;
}

export interface StockCampo {
  idAlmacen: string;
  idArticulo: string;
  codigo: string;
  descripcion: string;
  unidad: { id: string; codigo: string; decimales: number };
  controlaLotes: boolean;
  controlaSeries: boolean;
  saldo: string;
  pendienteValidar: string;
  disponible: string;
  lotes: { id: string; codigo: string; fechaCaducidad: string | null; saldo: string }[];
}

export interface PaqueteMantenimiento {
  partes: ParteCampo[];
  documentos: DocumentoCampo[];
  stock: { fotografiadoEn: string; almacenes: AlmacenCampo[]; filas: StockCampo[]; truncado: boolean };
}

/** `GET /api/mantenimiento/campo/partes/:id` — la vista de campo en línea (misma forma que el paquete). */
export interface ParteCampoEnLinea {
  parte: ParteCampo;
  documentos: DocumentoCampo[];
  stock: PaqueteMantenimiento['stock'];
}

/** FASE 8.2 — dominio operativo del trabajador (no su rol ni su competencia). */
export type EspecialidadOperativa = 'LIMPIEZA' | 'MANTENIMIENTO' | 'AMBAS';

export interface OperarioResumen {
  id: string;
  codigo: string;
  nombre: string;
  apellidos: string;
  tipo: 'FIJO' | 'VOLANTE' | 'GLOBAL';
  /** FASE 8.2. Opcional sólo para no romper respuestas anteriores a 0059. */
  especialidadOperativa?: EspecialidadOperativa;
  activo: boolean;
  nCentros: number;
}

export interface OperarioDetalle {
  id: string;
  codigo: string;
  nombre: string;
  apellidos: string;
  tipo: 'FIJO' | 'VOLANTE' | 'GLOBAL';
  especialidadOperativa?: EspecialidadOperativa;
  documentoIdentidad: string | null;
  telefono: string | null;
  fechaAlta: string;
  fechaBaja: string | null;
  activo: boolean;
}

export interface CentroOperario {
  idCentro: string;
  codigo: string;
  descripcion: string;
  esPrincipal: boolean;
  idCliente: string;
  clienteCodigo: string;
  clienteRazonSocial: string;
}

export interface Polivalencia {
  id: string;
  codigo: string;
  descripcion: string;
  mesesValidez: number | null;
  activo: boolean;
}

export type EstadoPolivalenciaOperario = 'SIN_EVALUAR' | 'VIGENTE' | 'CADUCADA';

export interface FilaMatrizPolivalencia {
  idPolivalencia: string;
  codigo: string;
  descripcion: string;
  activo: boolean;
  nivelActual: number | null;
  fechaInicio: string | null;
  ultimaRevision: string | null;
  caducidad: string | null;
  estado: EstadoPolivalenciaOperario;
}

export interface EvaluacionPolivalencia {
  idEvaluacion: string;
  nivel: number;
  fechaEvaluacion: string;
  fechaCaducidad: string | null;
  evaluadoPor: string;
  evaluadorNombre: string;
  observaciones: string | null;
  estado: 'ACTIVA' | 'ANULADA';
  anuladaEn: string | null;
  anuladaPor: string | null;
  anuladorNombre: string | null;
  motivoAnulacion: string | null;
}

export interface RequisitoTipo {
  idPolivalencia: string;
  polivalenciaCodigo: string;
  polivalenciaDescripcion: string;
  polivalenciaActiva: boolean;
  nivelMinimo: number;
  obligatorio: boolean;
}

export interface AptitudTipo {
  idTipo: string;
  tipoCodigo: string;
  tipoDescripcion: string;
  idCliente: string;
  clienteCodigo: string;
  clienteRazonSocial: string;
  apto: boolean;
  motivo: string | null;
}

export interface RequisitoAptitud {
  idPolivalencia: string;
  polivalenciaCodigo: string;
  polivalenciaDescripcion: string;
  nivelMinimo: number;
  obligatorio: boolean;
  nivelValido: number | null;
  cumple: boolean;
  motivo: string | null;
}

// ------------------------------------------------------- calidad (FASE 7.1)

/** Mismo vocabulario que `backend/src/calidad/tipos.ts` — un solo concepto de nivel (LEVE/GRAVE/CRITICA). */
export type EstadoIncidencia = 'ABIERTA' | 'EN_ANALISIS' | 'RESUELTA' | 'CERRADA' | 'CANCELADA';
export type EstadoAccionCorrectiva = 'PENDIENTE' | 'EN_CURSO' | 'COMPLETADA' | 'CANCELADA';

export interface EventoCalidad {
  id: string;
  fecha: string;
  sujeto: 'INCIDENCIA' | 'NC' | 'ACCION' | 'EVIDENCIA' | 'COMENTARIO';
  tipo: string;
  actorNombre: string | null;
  estadoAnterior: string | null;
  estadoNuevo: string | null;
  motivo: string | null;
  cambios: Record<string, { antes: unknown; despues: unknown }> | null;
  idIncidencia: string | null;
  idNc: string | null;
  idAccion: string | null;
  referencia: string | null;
}

export interface IncidenciaFila {
  id: string;
  codigo: string;
  estado: EstadoIncidencia;
  gravedad: GravedadNc;
  descripcion: string;
  visibleCliente: boolean;
  idCentro: string;
  centroNombre: string;
  idOt: string | null;
  otCodigo: string | null;
  idOtLinea: string | null;
  lineaDescripcion: string | null;
  /** Pre-F14.2 (0095): ubicación elegida al alta y su ruta de entonces. */
  idUbicacion?: string | null;
  ubicacion?: string | null;
  idResponsable: string | null;
  responsableNombre: string | null;
  fechaObjetivo: string | null;
  creadoPor: string;
  creadoPorNombre: string | null;
  creadoEn: string;
  idNc: string | null;
  codigoNc: string | null;
  estadoNc: string | null;
  /** FASE 7.2: fecha objetivo pasada (hoy del centro) y sin resolver. */
  vencida: boolean;
}

export interface IncidenciaDetalle extends IncidenciaFila {
  idCliente: string;
  otEstado: string | null;
  resolucion: string | null;
  motivoCancelacion: string | null;
  actualizadoEn: string;
  resueltaEn: string | null;
  cerradaEn: string | null;
  origenVinculo: 'ESCALADA' | 'VINCULADA' | null;
  /** FASE 7.2: NC cuyo cierre resolvió esta incidencia. */
  resueltaPorNc: { id: string; codigo: string } | null;
  lineaDeTiempo: EventoCalidad[];
}

export interface PaginaIncidencias {
  filas: IncidenciaFila[];
  total: number;
  pagina: number;
  porPagina: number;
}

export interface ResultadoEscalar {
  idIncidencia: string;
  idNc: string;
  codigoNc: string;
  idOt: string | null;
  estadoIncidencia: EstadoIncidencia;
}

export interface AccionCorrectiva {
  id: string;
  idNc: string;
  codigoNc: string;
  estadoNc: string;
  idCentro: string;
  centroNombre: string;
  descripcion: string;
  estado: EstadoAccionCorrectiva;
  idResponsable: string;
  responsableNombre: string | null;
  fechaObjetivo: string;
  vencida: boolean;
  resultado: string | null;
  motivoCancelacion: string | null;
  creadoEn: string;
  completadaEn: string | null;
  completadaPorNombre: string | null;
  evidencias: number;
  /** F11.2: compartida con el cliente. */
  visibleCliente?: boolean;
}

/** F11.2 — comentario de una NC visto por gestión. */
export interface ComentarioNc {
  id: string;
  texto: string;
  visibleCliente: boolean;
  deCliente: boolean;
  autorNombre: string | null;
  creadoEn: string;
}

export interface FichaCalidadNc {
  analisisCausa: string | null;
  visibleCliente: boolean;
  fechaObjetivo: string | null;
  acciones: AccionCorrectiva[];
  incidencias: { id: string; codigo: string; estado: EstadoIncidencia; descripcion: string; origen: string }[];
  lineaDeTiempo: EventoCalidad[];
  faltaParaCerrar: string[];
  /** F11.2 */
  comentarios?: ComentarioNc[];
  /** F11.2: el cliente participó (abrió, comentó o aportó evidencia): ya no vuelve a ser sólo interna. */
  participacionCliente?: boolean;
}

export interface ResponsableCandidato {
  id: string;
  nombre: string;
  rol: string;
}

export interface NcVinculable {
  id: string;
  codigo: string;
  descripcion: string;
  estado: string;
  otCodigo: string | null;
}

/** `GET /api/no-conformidades` (listado global, ya existente desde FASE 4.2-B). */
export interface PaginaNc {
  filas: NcFila[];
  total: number;
  pagina: number;
  porPagina: number;
}

// ------------------------------------------------ portal cliente de Calidad (FASE 7.3)

/** DTO PÚBLICO (backend `calidad/portal.ts`): nada interno viaja, así que nada interno puede pintarse. */
export interface EvidenciaPublica {
  id: string;
  ambito: string;
  contexto: string;
  visibilidad: 'CLIENTE';
  estado: 'ACTIVA';
  nombreOriginal: string;
  mime: string;
  tamanoBytes: number;
  descripcion: string | null;
  subidaEn: string;
  tomadaEn: string | null;
}

export interface HitoPublico {
  fecha: string;
  texto: string;
  /** F11.3: el motivo que escribió el cliente al cerrar o reabrir. */
  detalle?: string;
}

export interface IncidenciaPublicaFila {
  id: string;
  codigo: string;
  titulo: string;
  centro: { id: string; nombre: string };
  ot: { id: string; codigo: string } | null;
  nivel: string;
  estado: string;
  creadaEn: string;
  resueltaEn: string | null;
  nc: { id: string; codigo: string } | null;
  /** Portal v2 (0098): la ubicación reportada, si se indicó. Opcional para tolerar respuestas anteriores. */
  ubicacion?: string | null;
}

export interface IncidenciaPublicaDetalle extends IncidenciaPublicaFila {
  descripcion: string;
  linea: string | null;
  resolucion: string | null;
  evidencias: EvidenciaPublica[];
  evolucion: HitoPublico[];
}

export interface NcPublicaFila {
  id: string;
  codigo: string;
  titulo: string;
  centro: { id: string; nombre: string };
  ot: { id: string; codigo: string } | null;
  /** Gravedad OFICIAL; null = pendiente de clasificar por la empresa (F11.3). */
  gravedad: string | null;
  estado: string;
  origen: string;
  abiertaPorTi: boolean;
  fechaApertura: string;
  fechaCierre: string | null;
  /** F11.3 */
  ubicacion?: string | null;
  impactoCliente?: string | null;
  urgenciaCliente?: string | null;
  ultimaActividad?: string;
  novedadEmpresa?: boolean;
}

/** F11.2 — comentario visible para el cliente (autor público, nunca ids ni emails). */
export interface ComentarioPublico {
  id: string;
  texto: string;
  autor: { tipo: 'CLIENTE' | 'EMPRESA'; nombre: string };
  esTuyo: boolean;
  creadoEn: string;
}

/** F11.2 — acción correctiva compartida (sin responsable ni plazo). */
export interface AccionPublica {
  id: string;
  descripcion: string;
  estado: string;
  resultado: string | null;
  completadaEn: string | null;
  evidencias: EvidenciaPublica[];
}

/** F11.4 — indicadores PÚBLICOS de Calidad (sólo NC que el cliente ve; nada interno). */
export interface IndicadoresCalidadPublicos {
  periodo: { desde: string; hasta: string };
  /** Portal v2: centro al que se acota (null = todos). Los campos v2 son opcionales para tolerar respuestas anteriores. */
  idCentro?: string | null;
  activas: number;
  abiertas?: number;
  enTratamiento?: number;
  incidencias?: { abiertas: number; enAnalisis: number; registradas: number; resueltas: number; tiempoMedioResolucion: { horas: number | null; n: number } };
  registradas: number;
  cerradas: number;
  reaperturas: number;
  tiempoMedioResolucion: { horas: number | null; n: number };
  gravedad: { clave: string; etiqueta: string; total: number }[];
  evolucion: { mes: string; registradas: number; cerradas: number }[];
  porCentro: { idCentro: string; centro: string; registradas: number; activas: number }[];
  porUbicacion: { idCentro: string; centro: string; idUbicacion: string | null; ubicacion: string; registradas: number; activas: number; cerradas: number }[];
}

export interface NcPublicaDetalle extends NcPublicaFila {
  descripcion: string;
  resolucion: string | null;
  lineas: string[];
  incidencias: { id: string; codigo: string; titulo: string; estado: string }[];
  evidencias: EvidenciaPublica[];
  evolucion: HitoPublico[];
  /** F11.2 */
  comentarios?: ComentarioPublico[];
  acciones?: AccionPublica[];
  /** F11.3: queda alguna actuación en curso (sin detalle) — el cliente todavía no puede cerrar. */
  actuacionesEnCurso?: boolean;
  /** F11.3: cerrada por el cliente. */
  cerradaPorCliente?: boolean;
}

// ---------------------------------------------------------------- mantenimiento (FASE 8.1)

export type EstadoActivo = 'OPERATIVO' | 'FUERA_SERVICIO' | 'BAJA';

export interface RefActivo {
  id: string;
  codigo: string;
  nombre: string;
}

/** 0076: CLIENTE = equipo del cliente; EMPRESA = equipo de la empresa prestadora. */
export type TitularidadActivo = 'CLIENTE' | 'EMPRESA';

export interface ActivoResumen {
  id: string;
  codigo: string;
  nombre: string;
  estado: EstadoActivo;
  titularidad: TitularidadActivo;
  tipo: RefActivo;
  /** null en un activo EMPRESA. */
  cliente: RefActivo | null;
  /** null en un activo EMPRESA sin centro asignado. */
  centro: RefActivo | null;
  ubicacion: RefActivo | null;
}

export interface ActivoDetalle extends ActivoResumen {
  descripcion: string | null;
  fabricante: string | null;
  modelo: string | null;
  numeroSerie: string | null;
  referenciaCliente: string | null;
  fechaInstalacion: string | null;
  motivoEstado: string | null;
  bajaEn: string | null;
  bajaPor: { id: string; nombre: string } | null;
  creadoPor: { id: string; nombre: string };
  creadoEn: string;
  /** Versión para la edición (se devuelve como `versionEsperada`). */
  actualizadoEn: string;
}

export interface EventoActivo {
  id: string;
  tipo: 'ALTA' | 'ESTADO' | 'UBICACION' | 'EDICION' | 'BAJA';
  estadoAnterior: EstadoActivo | null;
  estadoNuevo: EstadoActivo | null;
  ubicacionAnterior: RefActivo | null;
  ubicacionNueva: RefActivo | null;
  cambios: Record<string, { antes: unknown; despues: unknown }> | null;
  motivo: string | null;
  usuario: { id: string; nombre: string };
  ocurridoEn: string;
}

export interface TipoActivo {
  id: string;
  codigo: string;
  nombre: string;
  activo: boolean;
  enUso: number;
}

export interface CentroDeActivos {
  id: string;
  codigo: string;
  nombre: string;
  cliente: { id: string; codigo: string; nombre: string };
}

export interface OpcionesActivos {
  centros: CentroDeActivos[];
  tipos: TipoActivo[];
}

export interface UbicacionElegible {
  id: string;
  codigo: string;
  nombre: string;
  tipo: string;
  ruta: string;
}

export interface QrActivo {
  ruta: string;
  codigo: string;
  nombre: string;
  centro: string;
  cliente: string;
}

// ---------------------------------------------------------------- partes de trabajo (FASE 8.2)

export type TipoPt = 'PREVENTIVO' | 'CORRECTIVO' | 'REVISION' | 'INSTALACION';
export type EstadoPt = 'ABIERTO' | 'PLANIFICADO' | 'EN_CURSO' | 'PENDIENTE' | 'FINALIZADO' | 'CERRADO' | 'ANULADO' | 'REABIERTO';
export type PrioridadMnt = 'BAJA' | 'MEDIA' | 'ALTA' | 'CRITICA';
export type MotivoPendiente = 'ESPERA_REPUESTO' | 'ESPERA_CLIENTE' | 'ESPERA_PROVEEDOR' | 'NUEVA_VISITA' | 'OTRO';
export type ResultadoIntervencion = 'RESUELTO' | 'NO_RESUELTO' | 'PARCIAL';

export interface ParteResumen {
  id: string;
  codigo: string;
  tipo: TipoPt;
  estado: EstadoPt;
  prioridad: PrioridadMnt;
  titulo: string;
  cliente: { id: string; nombre: string };
  centro: { id: string; nombre: string };
  fechaProgramada: string | null;
  horaProgramada: string | null;
  inicioProgramado: string | null;
  duracionEstimadaMin: number | null;
  motivoPendiente: MotivoPendiente | null;
  tecnicos: { idOperario: string; nombre: string }[];
  activos: { id: string; codigo: string; nombre: string; ubicacion?: string | null }[];
  /** FASE 8.3: plan preventivo del que salió (null si es manual) y su ocurrencia original. */
  planPreventivo: { id: string; codigo: string; nombre: string } | null;
  fechaOcurrencia: string | null;
  /** F12.1 — dónde: ubicación(es) de sus activos dentro del centro. */
  ubicacion?: string | null;
  /** F12.1 — tramo de «Mis partes» (lo calcula el servidor con la fecha local del centro). */
  franja?: FranjaPt;
  atrasado?: boolean;
}

export type FranjaPt = 'AHORA' | 'HOY' | 'PENDIENTE' | 'PROXIMO' | 'SIN_FECHA' | 'FINALIZADO';
/** F12.1 — lo que quien pregunta puede hacer ahora con el parte (lo decide el servidor). */
export type AccionPt = 'INICIAR' | 'RETOMAR' | 'FINALIZAR_INTERVENCION' | 'NUEVA_INTERVENCION' | 'PONER_PENDIENTE'
  | 'FINALIZAR' | 'CERRAR' | 'REABRIR' | 'PLANIFICAR' | 'RESOLVER_REAPERTURA' | 'EDITAR' | 'ANULAR';

export interface IntervencionPt {
  id: string;
  operario: { id: string; codigo: string; nombre: string };
  inicioEn: string;
  finEn: string | null;
  diagnostico: string | null;
  trabajoRealizado: string | null;
  resultado: ResultadoIntervencion | null;
  /** FASE 9.5-D — `operario` ejecuta; `registradaPor` la inició en el sistema (puede ser un gestor); `finalizadaPor` la cerró. */
  registradaPor?: { id: string; nombre: string };
  registradaPorEjecutor?: boolean;
  finalizadaPor?: { id: string; nombre: string } | null;
}

export interface ParteDetalle extends ParteResumen {
  descripcion: string | null;
  fechaObjetivo: string | null;
  detallePendiente: string | null;
  observacionesCierre: string | null;
  motivoAnulacion: string | null;
  direccionCentro: string | null;
  zonaHoraria: string;
  creadoPor: { id: string; nombre: string };
  creadoEn: string;
  actualizadoEn: string;
  finalizadoEn: string | null;
  cerradoEn: string | null;
  cerradoPor: { id: string; nombre: string } | null;
  tecnicosDetalle: { idOperario: string; codigo: string; nombre: string; especialidadOperativa: string; asignadoEn: string }[];
  activosDetalle: { id: string; codigo: string; nombre: string; estado: string; tipo: string; ubicacion: string | null }[];
  intervenciones: IntervencionPt[];
  /** F12.2 — `porResolver`: todo el trabajo de esa incidencia está terminado y sigue abierta. */
  incidencias: { id: string; codigo: string; titulo: string; estado: string; porResolver?: boolean }[];
  /** F12.2 — lo abrió el cliente desde su área. */
  solicitadoPorCliente?: boolean;
  soyTecnicoAsignado: boolean;
  miIntervencionAbierta: string | null;
  /** FASE 8.4 — derivado: viene de un plan preventivo o atiende una incidencia abierta por el cliente. */
  visibleCliente: boolean;
  /** FASE 8.4 — único texto del PT que llega al cliente. */
  resumenPublico: string | null;
  /** FASE 9.5-B — material sin resolver (lo cuenta la base). Cerrar exige cero en los dos. */
  materialPendiente: { consumos: number; devoluciones: number };
  /** FASE 9.5-C — última reapertura (interna). null si nunca se reabrió. */
  reapertura?: { motivo: string; detalle: string; resolucion: string | null; por: { id: string; nombre: string } | null; en: string | null } | null;
  /** F12.1 — acciones permitidas ahora a quien pregunta; la UI no recalcula permisos. */
  acciones?: AccionPt[];
  /** F12.1 — al cerrar, ¿hay que dejar resumen para el cliente? */
  resumenObligatorioAlCerrar?: boolean;
}

export type SeveridadMnt = 'BLOQUEANTE' | 'AVISO' | 'INFO';

/** FASE 8.3: alerta de programación de Mantenimiento (transversal: incluye OT de Limpieza de personas AMBAS). */
export interface AlertaMnt {
  severidad: SeveridadMnt;
  tipo:
    | 'SOLAPAMIENTO' | 'CONFLICTO_LIMPIEZA' | 'FUERA_DISPONIBILIDAD' | 'NO_DISPONIBLE'
    | 'TECNICO_NO_VALIDO' | 'SIN_TECNICO' | 'ACTIVO_BAJA' | 'SIN_DIA_OPERATIVO'
    | 'FESTIVO_DESPLAZADO' | 'FESTIVO_SALTADO' | 'OMITIDA_POR_EXCEPCION' | 'YA_MATERIALIZADA';
  mensaje: string;
  idOperario?: string;
}

export type AvisoProgramacion = AlertaMnt;

export interface EventoPt {
  id: string;
  tipo: string;
  estadoAnterior: string | null;
  estadoNuevo: string | null;
  operario: string | null;
  activo: string | null;
  incidencia: string | null;
  cambios: Record<string, unknown> | null;
  motivo: string | null;
  usuario: { id: string; nombre: string };
  /** FASE 9.5-D — en eventos de intervención, ¿actuó el propio ejecutor? (false = un gestor en su nombre) */
  actorEsEjecutor?: boolean | null;
  ocurridoEn: string;
}

export interface TecnicoElegible {
  idOperario: string;
  codigo: string;
  nombre: string;
  especialidadOperativa: string;
}

// ---------------------------------------------------------------- preventivos (FASE 8.3)

export type EstadoPlanPreventivo = 'BORRADOR' | 'VIGENTE' | 'PAUSADO' | 'CERRADO';
export type PoliticaFestivo = 'EJECUTAR' | 'SALTAR' | 'DESPLAZAR' | 'DESPLAZAR_ANTERIOR';

export interface PlanPreventivoResumen {
  id: string;
  codigo: string;
  nombre: string;
  tipoTrabajo: string;
  estado: EstadoPlanPreventivo;
  prioridad: PrioridadMnt;
  cliente: { id: string; nombre: string };
  centro: { id: string; nombre: string };
  activo: { id: string; codigo: string; nombre: string; estado: string } | null;
  rrule: string;
  frecuencia: string;
  fechaInicio: string;
  fechaFin: string | null;
  horaProgramada: string;
  duracionEstimadaMin: number;
  politicaFestivo: PoliticaFestivo;
  tecnicos: { idOperario: string; nombre: string }[];
  /** F12.3 — la REAL (con excepciones y festivos). */
  proximaFecha: string | null;
  /** F12.3 — si la próxima se desplazó por festivo: la que le tocaba. */
  proximaFechaOriginal?: string | null;
  /** F12.3 — cuándo se terminó por última vez un parte del plan. */
  ultimaEjecucion?: string | null;
}

/** F12.3 — generación automática de preventivos. */
export interface GeneracionPreventivos {
  automatica: boolean;
  horizonteDias: number;
  intervaloHoras: number;
  ultimaGeneracion: string | null;
}

export interface PlanPreventivoDetalle extends PlanPreventivoResumen {
  descripcion: string | null;
  zonaHoraria: string;
  excepciones: { id: string; fecha: string; tipo: 'INCLUIR' | 'EXCLUIR'; motivo: string }[];
  creadoPor: { id: string; nombre: string };
  creadoEn: string;
  actualizadoEn: string;
  nPartesGenerados: number;
  /** F12.3 — ocurrencias ya pasadas que se quedaron sin parte (no se generan hacia atrás: se avisa). */
  ocurrenciasNoGeneradas?: string[];
  /** F12.3 — última ocurrencia que ya tiene parte: cambiar el plan no la reescribe. */
  generadosHasta?: string | null;
}

export interface OcurrenciaPlan {
  fechaOcurrencia: string;
  fecha: string;
  hora: string;
  inicio: string | null;
  estado: 'NUEVA' | 'MATERIALIZADA' | 'OMITIDA' | 'SIN_DIA_OPERATIVO' | 'BLOQUEADA';
  parte: { id: string; codigo: string; estado: string } | null;
  tecnicos: { idOperario: string; nombre: string }[];
  alertas: AlertaMnt[];
}

export interface ProyeccionPlan {
  plan: { id: string; codigo: string; estado: EstadoPlanPreventivo; nombre: string };
  desde: string;
  hasta: string;
  materializable: boolean;
  nota: string | null;
  tecnicosPrevistos: { idOperario: string; nombre: string; valido: boolean; motivo: string | null }[];
  ocurrencias: OcurrenciaPlan[];
  resumen: { total: number; nuevas: number; materializadas: number; omitidas: number; bloqueadas: number; conConflictos: number };
}

export interface ResultadoMaterializacion {
  creados: { id: string; codigo: string; fechaOcurrencia: string; estado: string }[];
  yaMaterializadas: number;
  omitidas: number;
  bloqueadas: number;
  alertas: { fechaOcurrencia: string; alertas: AlertaMnt[] }[];
}

export interface EventoPp {
  id: string;
  tipo: string;
  estadoAnterior: string | null;
  estadoNuevo: string | null;
  operario: string | null;
  parte: string | null;
  idParte: string | null;
  fecha: string | null;
  cambios: Record<string, unknown> | null;
  motivo: string | null;
  usuario: { id: string; nombre: string };
  ocurridoEn: string;
}

export interface PlannerMantenimiento {
  desde: string;
  hasta: string;
  partes: ParteResumen[];
  proyecciones: (OcurrenciaPlan & { plan: { id: string; codigo: string; nombre: string }; centro: { id: string; nombre: string } })[];
  conflictos: { idPt: string; alertas: AlertaMnt[] }[];
}

// ---------------------------------------------------------------- incidencias de mantenimiento (FASE 8.2)

export type EstadoIncidenciaMnt = 'ABIERTA' | 'EN_REVISION' | 'RESUELTA' | 'CANCELADA';

export interface IncidenciaMntResumen {
  id: string;
  codigo: string;
  titulo: string;
  prioridad: PrioridadMnt;
  estado: EstadoIncidenciaMnt;
  cliente: { id: string; nombre: string };
  centro: { id: string; nombre: string };
  activo: { id: string; codigo: string; nombre: string; estado: string; ubicacion?: string | null } | null;
  creadoEn: string;
  nPartes: number;
  /** FASE 8.4 — CLIENTE = abierta desde el área cliente (y visible allí). */
  origen: 'INTERNO' | 'CLIENTE';
}

export interface IncidenciaMntDetalle extends IncidenciaMntResumen {
  descripcion: string | null;
  resolucion: string | null;
  resolucionPublica: string | null;
  motivoCancelacion: string | null;
  creadoPor: { id: string; nombre: string };
  resueltoEn: string | null;
  resueltoPor: { id: string; nombre: string } | null;
  actualizadoEn: string;
  partes: { id: string; codigo: string; tipo: string; estado: string; titulo: string }[];
  /** F12.2 — todo su trabajo terminado y sigue abierta: se ofrece resolverla. */
  porResolver?: boolean;
  /** F12.2 — resolución propuesta a partir de lo hecho en sus partes (editable). */
  propuestaResolucion?: { resolucion: string; resolucionPublica: string | null } | null;
  /** F12.2 — volvió a revisión al reabrirse un parte suyo: cuál, cuándo y la resolución que dejó de valer. */
  vueltaARevision?: { parte: { id: string; codigo: string } | null; en: string; resolucionAnterior: string | null } | null;
}

/** F12.2 — Mantenimiento en «Hoy». */
export interface HoyMantenimiento {
  fecha: string;
  hoy: ParteResumen[];
  atrasados: ParteResumen[];
  pendientes: ParteResumen[];
  sinTecnico: ParteResumen[];
  incidencias: { id: string; codigo: string; titulo: string; prioridad: string; situacion: 'SIN_PARTE' | 'POR_RESOLVER' }[];
  /** F12.3 — partes del día con conflicto, explicado. */
  conflictos?: { parte: ParteResumen; alertas: AlertaMnt[] }[];
}

/** F12.2 — histórico consolidado del activo. */
export interface EvidenciaHistorico { id: string; nombre: string; visibilidad: 'CLIENTE' | 'INTERNA' }
export type ItemHistoricoActivo =
  | {
    tipo: 'PARTE'; fecha: string; id: string; codigo: string; titulo: string; tipoPt: string; estado: string;
    centro: { id: string; nombre: string }; preventivo: { id: string; nombre: string } | null; resumenPublico: string | null;
    intervenciones: { id: string; tecnico: string; inicioEn: string; finEn: string | null; resultado: string | null;
      diagnostico: string | null; trabajoRealizado: string | null; evidencias: EvidenciaHistorico[] }[];
    evidencias: EvidenciaHistorico[];
  }
  | { tipo: 'INCIDENCIA'; fecha: string; id: string; codigo: string; titulo: string; estado: string; origen: 'INTERNO' | 'CLIENTE';
    centro: { id: string; nombre: string }; resolucion: string | null }
  | { tipo: 'ACTIVO'; fecha: string; evento: EventoActivo };

export interface EventoIm {
  id: string;
  tipo: string;
  estadoAnterior: string | null;
  estadoNuevo: string | null;
  parte: string | null;
  cambios: Record<string, unknown> | null;
  motivo: string | null;
  usuario: { id: string; nombre: string };
  ocurridoEn: string;
}

export interface EvidenciaMnt {
  id: string;
  ambito: string;
  contexto: string;
  idIntervencion: string | null;
  estado: 'ACTIVA' | 'ANULADA';
  visibilidad: 'INTERNA' | 'CLIENTE';
  nombreOriginal: string;
  mime: string;
  tamanoBytes: number;
  descripcion: string | null;
  subidaEn: string;
  tomadaEn: string | null;
  subidaPor: string;
  motivoAnulacion: string | null;
}

// ---------------------------------------------------------------- área cliente de mantenimiento (FASE 8.4)
//  DTO PÚBLICOS: lo que el servidor manda al cliente, campo a campo. Nunca
//  técnicos, autores, textos internos ni el plan preventivo.

export interface RefPublica { id: string; nombre: string }
export interface ActivoRefPublico { id: string; codigo: string; nombre: string }
export interface HitoPublicoMnt { fecha: string; texto: string; actor: 'TI' | 'EQUIPO' | null }

export interface EvidenciaPublicaMnt {
  id: string;
  contexto: string;
  nombreOriginal: string;
  mime: string;
  tamanoBytes: number;
  descripcion: string | null;
  subidaEn: string;
  subidaPorTi: boolean;
}

export interface IncidenciaMntPublica {
  id: string;
  codigo: string;
  titulo: string;
  centro: RefPublica;
  activo: ActivoRefPublico | null;
  urgencia: PrioridadMnt;
  estado: 'ABIERTA' | 'EN_REVISION' | 'RESUELTA' | 'CANCELADA';
  estadoTexto: string;
  creadaEn: string;
  creadaPorTi: boolean;
  partes: { id: string; codigo: string; estadoTexto: string }[];
}

export interface IncidenciaMntPublicaDetalle extends IncidenciaMntPublica {
  descripcion: string | null;
  resolucion: string | null;
  resueltaEn: string | null;
  admiteEvidencias: boolean;
  evidencias: EvidenciaPublicaMnt[];
  evolucion: HitoPublicoMnt[];
  /** F12.2 — estuvo resuelta y ha vuelto a revisión. */
  vueltaARevision?: boolean;
}

export interface PartePublico {
  id: string;
  codigo: string;
  tipo: TipoPt;
  tipoTexto: string;
  titulo: string;
  centro: RefPublica;
  activos: ActivoRefPublico[];
  estado: EstadoPt;
  estadoTexto: string;
  pendienteTexto: string | null;
  fechaProgramada: string | null;
  horaProgramada: string | null;
  duracionAproxMin: number | null;
  programadoEnPlan: boolean;
}

export interface PartePublicoDetalle extends PartePublico {
  resumen: string | null;
  finalizadoEn: string | null;
  cerradoEn: string | null;
  visitas: { fecha: string; estadoTexto: string; resultadoTexto: string | null }[];
  incidencias: { id: string; codigo: string; titulo: string }[];
  evidencias: EvidenciaPublicaMnt[];
  evolucion: HitoPublicoMnt[];
}

export interface ActivoPublico {
  id: string;
  codigo: string;
  nombre: string;
  tipo: string;
  centro: RefPublica;
  ubicacion: string | null;
  estado: string;
  estadoTexto: string;
  /** 0076: «Cliente» / «Empresa prestadora». */
  titularidad: TitularidadActivo;
  titularidadTexto: string;
  fabricante: string | null;
  modelo: string | null;
}

export interface ActivoPublicoDetalle extends ActivoPublico {
  referenciaCliente: string | null;
  fechaInstalacion: string | null;
  partesRecientes: PartePublico[];
  proximos: PartePublico[];
  documentos: EvidenciaPublicaMnt[];
  /** F12.2 — puede solicitar mantenimiento sobre él. */
  puedeSolicitar?: boolean;
}

export interface ResumenMantenimientoPublico {
  incidenciasAbiertas: number;
  partesEnCurso: number;
  partesPendientes: number;
  proximos: PartePublico[];
}

// ---------------------------------------------------------------- inventario (FASE 9.1)
//  Maestros, artículos y almacenes. SIN stock: cantidades configuradas
//  (mínimo, máximo, punto de pedido) como texto decimal exacto.

export interface RefInventario { id: string; codigo: string; descripcion: string }
export interface UnidadMedida { id: string; codigo: string; descripcion: string; decimales: number; activo: boolean }
export interface TipoArticulo extends RefInventario { activo: boolean; nFamilias: number }
export interface FamiliaArticulo extends RefInventario { activo: boolean; tipo: RefInventario; nSubfamilias: number }
export interface SubfamiliaArticulo extends RefInventario { activo: boolean; familia: RefInventario; tipo: RefInventario }
export type TipoAlmacen = 'CENTRAL' | 'CENTRO' | 'MOVIL' | 'OTRO';

export interface OpcionesInventario {
  unidades: UnidadMedida[];
  tipos: TipoArticulo[];
  familias: FamiliaArticulo[];
  subfamilias: SubfamiliaArticulo[];
  centros: { id: string; nombre: string; cliente: string }[];
  tiposAlmacen: TipoAlmacen[];
}

export interface ArticuloResumen {
  id: string;
  /** ART-000001: token NeatOS (inmutable; etiquetas, soporte). No se muestra por defecto. */
  codigo: string;
  /** F13.5-A: lo que ve el usuario (el código del maestro o, sin él, el token). Almacenado en el servidor. */
  codigoNegocio: string;
  /** = `codigo`. */
  tokenNeatos: string;
  /** Sistema del que procede el código de negocio; null = es el token. */
  sistemaCodigoNegocio: string | null;
  descripcion: string;
  tipo: RefInventario;
  familia: RefInventario;
  subfamilia: RefInventario | null;
  unidadStock: RefInventario & { decimales: number };
  inventariable: boolean;
  activo: boolean;
}

export interface ConversionArticulo {
  id: string;
  unidad: RefInventario & { decimales: number };
  factor: string;
  esCompra: boolean;
  esConsumo: boolean;
  activo: boolean;
  actualizadoEn: string;
}

export interface ArticuloDetalle extends ArticuloResumen {
  controlaLotes: boolean;
  controlaSeries: boolean;
  controlaCaducidad: boolean;
  creadoPor: { id: string; nombre: string };
  creadoEn: string;
  actualizadoEn: string;
  conversiones: ConversionArticulo[];
  /** FASE 9.1-C — virgen (sin uso operativo): sólo entonces ofrece «Eliminar definitivamente». */
  eliminable: boolean;
  clasificacionActiva: { tipo: boolean; familia: boolean; subfamilia: boolean | null; unidad: boolean };
}

export interface AlmacenResumen {
  id: string;
  /** ALM-…: token NeatOS. Lo que se muestra es `codigoNegocio`. */
  codigo: string;
  /** F13.5-A: FUR-MNT-01, código del ERP… (por defecto, el token). */
  codigoNegocio: string;
  descripcion: string;
  tipo: TipoAlmacen;
  centro: { id: string; nombre: string; cliente: string } | null;
  permiteStockNegativo: boolean;
  inventariable: boolean;
  activo: boolean;
  editable: boolean;
}

export interface AlmacenDetalle extends AlmacenResumen {
  creadoPor: { id: string; nombre: string };
  creadoEn: string;
  actualizadoEn: string;
  /** F13.8: su código en cada sistema (SAP A001, SAGE 07…). */
  referencias?: { sistema: { codigo: string; nombre: string }; codigoExterno: string; activo: boolean; origen: string; importacion: string | null; maestro: boolean }[];
}

export interface ConfigArticuloAlmacen {
  id: string;
  articulo: { id: string; codigo: string; codigoNegocio?: string; descripcion: string; unidad: string; activo: boolean };
  almacen: { id: string; codigo: string; codigoNegocio?: string; descripcion: string; tipo: TipoAlmacen; centro: string | null; activo: boolean };
  stockMinimo: string | null;
  stockMaximo: string | null;
  puntoPedido: string | null;
  inventariable: boolean;
  activo: boolean;
  editable: boolean;
  actualizadoEn: string;
}

export interface EventoInventario {
  id: string;
  tipo: string;
  almacen: string | null;
  unidad: string | null;
  cambios: Record<string, unknown> | null;
  usuario: { id: string; nombre: string };
  ocurridoEn: string;
}

// ---------------------------------------------------------------- stock (FASE 9.2)
//  El stock NO se edita: se lee (saldos y ledger) y se mueve con operaciones
//  que sólo cuentan al CONFIRMARSE. Cantidades como texto decimal exacto.

/** FASE 9.5: CONSUMO_PT lo genera la validación de consumos de un parte; FASE 9.6-A: CONSUMO_OT, la de una OT (nunca a mano). */
export type TipoOperacionStock = 'ENTRADA' | 'SALIDA' | 'TRASLADO' | 'AJUSTE' | 'DEVOLUCION' | 'CONSUMO_PT' | 'CONSUMO_OT';
export type EstadoOperacionStock = 'BORRADOR' | 'CONFIRMADA' | 'ANULADA';
export type SentidoStock = 'ENTRADA' | 'SALIDA';
export type EstadoStock = 'SIN_STOCK' | 'BAJO' | 'NORMAL' | 'SOBRESTOCK';

export interface RefAlmacenStock { id: string; codigo: string; codigoNegocio?: string; descripcion: string; centro: string | null }
export interface RefUnidadStock { id: string; codigo: string; decimales: number }

/** FASE 9.4: control de trazabilidad del artículo. */
export interface Trazabilidad { controlaLotes: boolean; controlaSeries: boolean; controlaCaducidad: boolean }

export interface SaldoStock {
  articulo: { id: string; codigo: string; codigoNegocio?: string; descripcion: string; activo: boolean } & Partial<Trazabilidad>;
  almacen: RefAlmacenStock & { activo: boolean };
  unidad: RefUnidadStock;
  saldo: string;
  stockMinimo: string | null;
  puntoPedido: string | null;
  stockMaximo: string | null;
  estado: EstadoStock;
  configuracionActiva: boolean;
  actualizadoEn: string;
}

export interface OpcionAlmacenStock extends RefAlmacenStock { tipo: TipoAlmacen; permiteStockNegativo: boolean }
export interface OpcionArticuloStock {
  id: string;
  codigo: string;
  descripcion: string;
  unidadStock: RefUnidadStock;
  unidades: (RefUnidadStock & { factor: string })[];
  saldo: string | null;
  /** FASE 9.4 (opcional para compatibilidad con respuestas anteriores). */
  trazabilidad?: Trazabilidad;
}

export interface LineaOperacionStock {
  id: string;
  orden: number;
  articulo: { id: string; codigo: string; codigoNegocio?: string; descripcion: string; unidadStock: RefUnidadStock } & Partial<Trazabilidad>;
  /** FASE 9.4: lote de la línea y series (una por unidad). */
  lote?: { id: string; codigo: string; fechaCaducidad: string | null; caducado: boolean } | null;
  series?: { id: string; numero: string }[];
  cantidad: string;
  unidad: RefUnidadStock;
  sentido: SentidoStock | null;
  observaciones: string | null;
  /** FASE 9.6-A: consumo de OT que generó la línea (y su línea de OT). */
  consumoOt?: { id: string; idLinea: string } | null;
  factorConversion: string | null;
  cantidadStock: string | null;
  saldoOrigen: string | null;
  saldoDestino: string | null;
  actualizadoEn: string;
}

export interface OperacionStockResumen {
  id: string;
  codigo: string;
  tipo: TipoOperacionStock;
  estado: EstadoOperacionStock;
  almacen: RefAlmacenStock;
  almacenDestino: RefAlmacenStock | null;
  tipoDocumento: string | null;
  numeroDocumento: string | null;
  /** F13.5-B: fecha del documento de origen (AAAA-MM-DD); la del movimiento es la de confirmación. */
  fechaDocumento?: string | null;
  nLineas: number;
  creadoPor: { id: string; nombre: string };
  creadoEn: string;
  confirmadoEn: string | null;
  origen: { id: string; codigo: string } | null;
  /** FASE 9.5: parte de trabajo de origen (consumo o devolución de material). */
  parte?: { id: string; codigo: string } | null;
  /** FASE 9.6-A: OT de Limpieza de origen (consumos validados). */
  ot?: { id: string; codigo: string } | null;
}

export interface OperacionStockDetalle extends OperacionStockResumen {
  observaciones: string | null;
  motivo: string | null;
  motivoAnulacion: string | null;
  confirmadoPor: { id: string; nombre: string } | null;
  anuladoPor: { id: string; nombre: string } | null;
  anuladoEn: string | null;
  actualizadoEn: string;
  lineas: LineaOperacionStock[];
  rectificaciones: { id: string; codigo: string; tipo: TipoOperacionStock; estado: EstadoOperacionStock }[];
  /** FASE 9.3: inventario físico que generó este AJUSTE. */
  inventario?: { id: string; codigo: string } | null;
}

export interface EventoOperacionStock {
  id: string;
  tipo: string;
  idLinea: string | null;
  cambios: Record<string, unknown> | null;
  usuario: { id: string; nombre: string };
  ocurridoEn: string;
}

export interface MovimientoStock {
  id: string;
  operacion: {
    id: string; codigo: string; tipo: TipoOperacionStock; visible: boolean; parte?: { id: string; codigo: string } | null;
    /** FASE 9.6-A: OT, línea y consumo de este movimiento. */
    ot?: { id: string; codigo: string; idLinea: string | null; idConsumo: string | null } | null;
  };
  articulo: { id: string; codigo: string; codigoNegocio?: string; descripcion: string };
  almacen: RefAlmacenStock;
  sentido: SentidoStock;
  /** FASE 9.4. */
  lote?: { id: string; codigo: string } | null;
  serie?: { id: string; numero: string } | null;
  cantidadStock: string;
  unidadStock: string;
  cantidadOrigen: string;
  unidadOrigen: string;
  factorConversion: string;
  creadoPor: { id: string; nombre: string };
  creadoEn: string;
}

// ---------------------------------------------------------------- inventarios físicos (FASE 9.3)
//  El inventario no toca el stock: cuenta y, al cerrar, la base genera UN
//  AJUSTE de 9.2. Teórico y diferencia sólo llegan cuando la API decide
//  enseñarlos (conteo ciego).

export type EstadoInventarioFisico = 'BORRADOR' | 'EN_CONTEO' | 'PENDIENTE_VALIDACION' | 'CERRADO' | 'ANULADO';
export type AlcanceInventario = 'TOTAL' | 'PARCIAL';

export interface InventarioFisicoResumen {
  id: string;
  codigo: string;
  almacen: { id: string; codigo: string; codigoNegocio?: string; descripcion: string; centro: string | null; activo: boolean };
  alcance: AlcanceInventario;
  estado: EstadoInventarioFisico;
  conteoCiego: boolean;
  progreso: { total: number; contadas: number };
  creadoPor: { id: string; nombre: string };
  creadoEn: string;
  iniciadoEn: string | null;
  cerradoEn: string | null;
  operacionAjuste: { id: string; codigo: string } | null;
}

export interface InventarioFisicoDetalle extends InventarioFisicoResumen {
  observaciones: string | null;
  iniciadoPor: { id: string; nombre: string } | null;
  enviadoValidacionPor: { id: string; nombre: string } | null;
  enviadoValidacionEn: string | null;
  cerradoPor: { id: string; nombre: string } | null;
  anuladoPor: { id: string; nombre: string } | null;
  anuladoEn: string | null;
  motivoAnulacion: string | null;
  actualizadoEn: string;
  veDiferencias: boolean;
  veSaldoActual: boolean;
  resumen: {
    total: number; contadas: number; pendientes: number; recontadas: number;
    conDiferencia: number | null; sinDiferencia: number | null; positivas: number | null; negativas: number | null;
  };
}

export interface ConteoInventarioFisico {
  id: string;
  numero: number;
  cantidad: string;
  unidad: string;
  factorConversion: string;
  cantidadStock: string;
  observaciones: string | null;
  contadoPor: { id: string; nombre: string };
  contadoEn: string;
  teorico: string | null;
  diferencia: string | null;
  /** FASE 9.4 (serializados, si se ven diferencias): faltantes + inesperadas. */
  seriesDiscrepantes?: number | null;
}

/** FASE 9.4: conteo por identidad de una línea serializada. */
export interface SeriesLineaInventario {
  encontradas: string[];
  esperadas: number | null;
  faltantes: string[] | null;
  inesperadas: string[] | null;
}

export interface LineaInventarioFisico {
  id: string;
  articulo: { id: string; codigo: string; codigoNegocio?: string; descripcion: string; activo: boolean } & Partial<Trazabilidad>;
  /** FASE 9.4. */
  lote?: { id: string; codigo: string; fechaCaducidad: string | null; caducado: boolean; activo: boolean } | null;
  serializado?: boolean;
  series?: SeriesLineaInventario | null;
  seriesEnStock?: string[] | null;
  unidadStock: RefUnidadStock;
  unidades: (RefUnidadStock & { factor: string })[];
  estadoConteo: 'PENDIENTE' | 'CONTADA' | 'RECONTADA';
  nConteos: number;
  vigente: ConteoInventarioFisico | null;
  saldoInicio: string | null;
  saldoActual: string | null;
  saldoTrasAjuste: string | null;
}

export interface EventoInventarioFisico {
  id: string;
  tipo: string;
  cambios: Record<string, unknown> | null;
  usuario: { id: string; nombre: string };
  ocurridoEn: string;
}

// ---------------------------------------------------------------- trazabilidad (FASE 9.4)
//  Lotes y series: identidad (catálogo interno) + situación derivada del
//  ledger (saldo por lote, ubicación de la serie) con el ámbito de stock.

export interface CaducidadLote {
  estado: 'VIGENTE' | 'CADUCADO' | null;
  /** Días hasta la caducidad (negativo: caducado hace N días). */
  dias: number | null;
  /** F13.2: PROXIMA = caduca en ≤ 30 días. Aviso; nunca bloquea. */
  situacion?: 'SIN_CADUCIDAD' | 'VIGENTE' | 'PROXIMA' | 'CADUCADA';
}
export interface ArticuloTrazable {
  id: string; codigo: string; descripcion: string; controlaCaducidad: boolean; controlaSeries: boolean; unidad: string;
  /** F13.5-A: código de negocio (o ART-…). */
  codigoNegocio?: string;
}
/** F13.2: ACTIVO o BLOQUEADO (no sale, no se consume ni se traslada). */
export type EstadoLote = 'ACTIVO' | 'BLOQUEADO';
export type TipoIdentificadorLote = 'CODE128' | 'CODE39' | 'QR' | 'DATAMATRIX' | 'OTRO';
export interface IdentificadorLote { id: string; tipo: TipoIdentificadorLote; valor: string; activo: boolean; creadoEn: string; motivoDesactivacion: string | null }
export interface PayloadEtiqueta { url: string; compacto: string }
export interface EtiquetaVista extends PayloadEtiqueta {
  tipo: 'ART' | 'LOT';
  articulo: { id: string; codigo: string; codigoNegocio: string; descripcion: string };
  lote: { id: string; codigo: string; fechaCaducidad: string | null; estado: EstadoLote; activo: boolean } | null;
  /** Barras del Code 128 ('1'/'0', sin zona de silencio) o null. */
  code128: string | null;
  code128En: PlantillaEtiquetas[];
  avisos: string[];
}
export type PlantillaEtiquetas = 'A4_3x8' | 'A4_2x7';
export interface ResumenRegularizacion {
  id: string;
  codigo: string;
  tipo: 'ACTIVACION_DIRECTA' | 'REGULARIZACION';
  activaCaducidad: boolean;
  motivo: string;
  stockAntes: { almacen: { id: string; codigo: string; codigoNegocio?: string }; saldo: string }[];
  stockDespues: { almacen: { id: string; codigo: string; codigoNegocio?: string }; saldo: string }[];
  reparto: { almacen: { id: string; codigo: string; codigoNegocio?: string }; lote: { id: string | null; codigo: string }; cantidad: string; tecnico: boolean }[];
  operaciones: { id: string; codigo: string }[];
  creadoPor: string;
  creadoEn: string;
}
export interface PreviaRegularizacion {
  articulo: { id: string; codigo: string; codigoNegocio?: string; descripcion: string; activo: boolean; controlaLotes: boolean; controlaSeries: boolean; controlaCaducidad: boolean;
    unidad: { codigo: string; decimales: number } };
  stock: { almacen: { id: string; codigo: string; codigoNegocio?: string; descripcion: string }; saldo: string }[];
  total: string;
  bloqueos: string[];
  noAplica: string | null;
  regularizaciones: ResumenRegularizacion[];
}

export interface LoteResumen {
  id: string;
  codigo: string;
  articulo: ArticuloTrazable;
  fechaFabricacion: string | null;
  fechaCaducidad: string | null;
  caducidad: CaducidadLote;
  activo: boolean;
  stock: string;
  nAlmacenes: number;
  /** FASE 9.4-B: con stock / agotado (tuvo entradas, hoy 0) / sin entradas (creado sin movimientos). */
  situacion?: 'CON_STOCK' | 'AGOTADO' | 'SIN_ENTRADAS';
  /** F13.2 */
  estado?: EstadoLote;
  proveedor?: string | null;
  /** F13.2: día de la primera entrada en el ledger (derivada). */
  fechaRecepcion?: string | null;
}
export interface LoteDetalle extends LoteResumen {
  observaciones: string | null;
  /** F13.2 */
  bloqueo?: { motivo: string; por: { id: string; nombre: string }; en: string } | null;
  identificadores?: IdentificadorLote[];
  etiqueta?: PayloadEtiqueta;
  stockPorAlmacen: { almacen: { id: string; codigo: string; codigoNegocio?: string; descripcion: string }; saldo: string }[];
  nSeries: number;
  usado: boolean;
  creadoPor: { id: string; nombre: string };
  creadoEn: string;
  actualizadoEn: string;
}

export interface EstadoSerie {
  situacion: 'EN_STOCK' | 'FUERA' | 'SIN_INFORMACION';
  almacen: { id: string; codigo: string; codigoNegocio?: string; descripcion: string } | null;
}
export interface SerieResumen {
  id: string;
  numero: string;
  articulo: ArticuloTrazable;
  lote: { id: string; codigo: string; fechaCaducidad: string | null; caducidad: CaducidadLote } | null;
  estado: EstadoSerie;
  activo: boolean;
  ultimoMovimiento: string | null;
}
export interface SerieDetalle extends SerieResumen {
  observaciones: string | null;
  usada: boolean;
  creadoPor: { id: string; nombre: string };
  creadoEn: string;
  actualizadoEn: string;
}

export interface OpcionLote { id: string; codigo: string; fechaCaducidad: string | null; caducidad: CaducidadLote; saldo: string | null; activo: boolean; estado?: EstadoLote }
export interface OpcionSerie { id: string; numero: string; lote: string | null; idLote: string | null; enStock: boolean; activo: boolean }

export interface DetalleStock {
  articulo: { id: string; codigo: string; codigoNegocio?: string; descripcion: string; unidad: string } & Trazabilidad;
  almacen: { id: string; codigo: string; codigoNegocio?: string; descripcion: string };
  saldo: string;
  lotes: { id: string; codigo: string; fechaCaducidad: string | null; caducidad: CaducidadLote; saldo: string; series: { id: string; numero: string }[] }[];
  series: { id: string; numero: string; lote: string | null; idLote: string | null }[];
  seriesTruncadas: boolean;
  /** FASE 9.4-B. */
  lotesAgotados?: { id: string; codigo: string; fechaCaducidad: string | null; caducidad: CaducidadLote }[];
  lotesSinEntradas?: number;
}

export interface EventoTrazabilidad {
  id: string;
  tipo: string;
  cambios: Record<string, unknown> | null;
  usuario: { id: string; nombre: string };
  ocurridoEn: string;
}

// ---------------------------------------------------------------- consumos de PT (FASE 9.5)
//  Registrar un consumo NO mueve stock; validarlo genera la operación
//  CONSUMO_PT (motor de 9.2). Devolver = material que vuelve físicamente.

export type EstadoConsumoPt = 'BORRADOR' | 'PENDIENTE_VALIDACION' | 'VALIDADO' | 'RECHAZADO' | 'ANULADO';
export type EstadoDevolucionPt = 'PENDIENTE_VALIDACION' | 'VALIDADO' | 'RECHAZADO' | 'ANULADO';

export interface DevolucionPt {
  id: string;
  estado: EstadoDevolucionPt;
  cantidad: string;
  unidad: RefUnidadStock;
  cantidadStock: string;
  series: { id: string; numero: string }[];
  observaciones: string | null;
  registradoPor: { id: string; nombre: string };
  registradoEn: string;
  validadoPor: { id: string; nombre: string } | null;
  validadoEn: string | null;
  rechazadoPor: { id: string; nombre: string } | null;
  rechazadoEn: string | null;
  motivoRechazo: string | null;
  anuladoEn: string | null;
  operacion: { id: string; codigo: string } | null;
  version: number;
}

export interface ConsumoPt {
  id: string;
  estado: EstadoConsumoPt;
  articulo: { id: string; codigo: string; codigoNegocio?: string; descripcion: string; controlaLotes: boolean; controlaSeries: boolean; controlaCaducidad: boolean; unidadStock: RefUnidadStock };
  almacen: { id: string; codigo: string; codigoNegocio?: string; descripcion: string };
  cantidad: string;
  unidad: RefUnidadStock;
  factorConversion: string;
  cantidadStock: string;
  lote: { id: string; codigo: string; fechaCaducidad: string | null; caducado: boolean } | null;
  series: { id: string; numero: string }[];
  intervencion: { id: string; inicioEn: string; finEn: string | null; operario: string } | null;
  observaciones: string | null;
  registradoPor: { id: string; nombre: string };
  registradoEn: string;
  enviadoValidacionEn: string | null;
  validadoPor: { id: string; nombre: string } | null;
  validadoEn: string | null;
  rechazadoPor: { id: string; nombre: string } | null;
  rechazadoEn: string | null;
  motivoRechazo: string | null;
  anuladoPor: { id: string; nombre: string } | null;
  anuladoEn: string | null;
  motivoAnulacion: string | null;
  operacion: { id: string; codigo: string } | null;
  disponible: string | null;
  devuelto: string;
  devolucionPendiente: string;
  maximoDevolvible: string;
  devoluciones: DevolucionPt[];
  version: number;
}

export interface MaterialesPt {
  parte: { id: string; codigo: string; estado: string };
  resumen: { borradores: number; pendientes: number; validados: number; rechazados: number; anulados: number; devolucionesPendientes: number; devolucionesValidadas: number };
  consumos: ConsumoPt[];
  puede: { registrar: boolean; validar: boolean; rechazar: boolean; devolver: boolean };
  operaciones?: { id: string; codigo: string }[];
}

export interface AlmacenConsumo { id: string; codigo: string; codigoNegocio?: string; descripcion: string; tipo: string; centro: string | null; /** F13.4: la furgoneta del técnico. */ propio?: boolean }

// ---------------------------------------------------------------- almacenes móviles / furgonetas (F13.4)
//  Una furgoneta es un almacén MOVIL con su stock normal; aquí sólo su técnico responsable (con vigencia).
export interface RefTecnico { id: string; codigo: string; nombre: string }
export interface AsignacionMovil {
  id: string; tecnico: RefTecnico; desde: string; hasta: string | null; asignadoPor: string; motivo: string;
  sinRecuento: boolean; inventario: { id: string; codigo: string } | null; cerradoPor: string | null; motivoCierre: string | null;
}
export interface AlmacenMovilResumen {
  /** `codigo` = ALM-… (token); `codigoNegocio` = FUR-MNT-01 (lo que ve el técnico). */
  id: string; codigo: string; codigoNegocio: string; descripcion: string; centro: { id: string; descripcion: string } | null; activo: boolean;
  responsable: (RefTecnico & { desde: string }) | null;
  referencias: number; lotesBloqueados: number; lotesCaducados: number;
  ultimoRecuento: { id: string; codigo: string; cerradoEn: string } | null;
  recuentoAbierto: { id: string; codigo: string; estado: string } | null;
}
export interface AlmacenMovilDetalle extends AlmacenMovilResumen { historial: AsignacionMovil[]; conStock: boolean }
export interface LineaStockMovil {
  articulo: { id: string; codigo: string; codigoNegocio: string; descripcion: string; controlaLotes: boolean; controlaSeries: boolean };
  unidad: { codigo: string; decimales: number };
  lote: { id: string; codigo: string; fechaCaducidad: string | null; estado: 'ACTIVO' | 'BLOQUEADO'; caducado: boolean } | null;
  cantidad: string;
  series: string[];
}
export interface TecnicoAsignable extends RefTecnico { almacenActual: { id: string; codigo: string; codigoNegocio?: string } | null }
export interface MiAlmacenMovil {
  almacen: { id: string; codigo: string; codigoNegocio?: string; descripcion: string; desde: string } | null;
  stock: LineaStockMovil[];
  recuentoAbierto: { id: string; codigo: string; estado: string } | null;
}

// ---------------------------------------------------------------- consumos de OT de Limpieza (FASE 9.6-A)
//  Registrar NO mueve stock; validar genera la operación CONSUMO_OT (motor de 9.2).
//  Sin borrador ni series; un consumo = una línea de la OT y, si aplica, un lote.

export type EstadoConsumoOt = 'PENDIENTE_VALIDACION' | 'VALIDADO' | 'RECHAZADO' | 'ANULADO';

export interface ConsumoOt {
  id: string;
  idLinea: string;
  estado: EstadoConsumoOt;
  articulo: { id: string; codigo: string; codigoNegocio?: string; descripcion: string; controlaLotes: boolean; controlaCaducidad: boolean; unidadStock: RefUnidadStock };
  almacen: { id: string; codigo: string; codigoNegocio?: string; descripcion: string };
  cantidad: string;
  unidad: RefUnidadStock;
  factorConversion: string;
  cantidadStock: string;
  lote: { id: string; codigo: string; fechaCaducidad: string | null; caducado: boolean } | null;
  observaciones: string | null;
  ciclo: { id: string; desde: string } | null;
  registradoPor: { id: string; nombre: string };
  registradoEn: string;
  validadoPor: { id: string; nombre: string } | null;
  validadoEn: string | null;
  rechazadoPor: { id: string; nombre: string } | null;
  rechazadoEn: string | null;
  motivoRechazo: string | null;
  anuladoPor: { id: string; nombre: string } | null;
  anuladoEn: string | null;
  motivoAnulacion: string | null;
  operacion: { id: string; codigo: string } | null;
  /** FASE 9.6-B1: previsto al que corresponde (otro artículo = sustitución en ejecución). */
  previsto?: { id: string; codigo: string; descripcion: string } | null;
  disponible: string | null;
  version: number;
}

export interface ConsumosOt {
  ot: { id: string; codigo: string; estado: string };
  resumen: { pendientes: number; validados: number; rechazados: number; anulados: number };
  consumos: ConsumoOt[];
  cierreRetenido: boolean;
  puede: { registrar: boolean; validar: boolean; rechazar: boolean; anular: boolean };
  operaciones?: { id: string; codigo: string }[];
}

export interface AlmacenConsumoOt { id: string; codigo: string; codigoNegocio?: string; descripcion: string; tipo: string }
export interface AlmacenesConsumoOt { filas: AlmacenConsumoOt[]; preseleccion: string | null; codigo: 'SIN_ALMACEN_CENTRO' | null }
export interface LoteConsumoOt {
  id: string;
  codigo: string;
  fechaCaducidad: string | null;
  caducidad: CaducidadLote;
  saldo: string;
  activo: boolean;
  /** Sugerencia FEFO: nunca se elige sola. */
  recomendado: boolean;
}

// ---------------------------------------------------------------- material previsto (FASE 9.6-B1)
//  Previsto NO mueve ni reserva stock. Plan: material habitual de una tarea (sólo en BORRADOR).
//  OT: snapshot al emitir + previsión propia (añadir / modificar / retirar / sustituir).

export interface UnidadArticuloPrevisto { id: string; codigo: string; decimales: number; factor: string }

export interface MaterialPlan {
  id: string;
  articulo: { id: string; codigo: string; codigoNegocio?: string; descripcion: string; activo: boolean; unidadStock: RefUnidadStock; unidades: UnidadArticuloPrevisto[] };
  cantidad: string;
  unidad: RefUnidadStock;
  cantidadStock: string | null;
  orden: number;
  instrucciones: string | null;
  disponibleCentro: string;
  stockInferior: boolean;
}
export interface MaterialesTareaPlan {
  plan: { id: string; estado: string };
  editable: boolean;
  materiales: MaterialPlan[];
}

export type EstadoPrevisto = 'ACTIVO' | 'RETIRADO' | 'SUSTITUIDO';
export type IncidenciaPrevisto = 'ARTICULO_INACTIVO' | 'CONVERSION_INACTIVA' | 'CONVERSION_NO_DISPONIBLE';
export interface PrevistoOt {
  id: string;
  idLinea: string;
  estado: EstadoPrevisto;
  origen: 'PLAN' | 'OT' | 'REEMISION';
  articulo: { id: string; codigo: string; codigoNegocio?: string; descripcion: string; activo: boolean; unidades: UnidadArticuloPrevisto[] };
  cantidad: string;
  unidad: { id: string; codigo: string };
  unidadStock: string;
  previstaStock: string | null;
  instrucciones: string | null;
  orden: number;
  incidencia: IncidenciaPrevisto | null;
  sustituyeA: { id: string; codigo: string; descripcion: string } | null;
  sustituidoPor: { id: string; codigo: string; descripcion: string } | null;
  consumidaValidadaStock: string;
  consumidaPendienteStock: string;
  desviacionStock: string | null;
  sustituciones: { idArticulo: string; codigo: string; descripcion: string; unidadStock: string; validadaStock: string; pendienteStock: string }[];
  disponibleCentro: string;
  stockInferior: boolean;
  retirada: { en: string; por: string; motivo: 'ELIMINADO' | 'SUSTITUIDO'; observacion: string | null } | null;
  version: number;
  /** FASE 9.6-B2 (logística, unidad de stock; null salvo en la fila dueña del artículo en la línea). No es stock. */
  entregadaStock?: string | null;
  devueltaStock?: string | null;
  pendienteJustificarStock?: string | null;
  consumoSobreEntregadoStock?: string | null;
  /** Entregas de OTRO artículo enlazadas a este previsto (sustitución logística). */
  entregasSustitutas?: { idArticulo: string; codigo: string; descripcion: string; unidadStock: string; entregadaStock: string }[];
}
export interface NoPrevistoOt {
  idLinea: string;
  articulo: { id: string; codigo: string; codigoNegocio?: string; descripcion: string };
  unidadStock: string;
  validadaStock: string;
  pendienteStock: string;
  entregadaStock?: string | null;
  devueltaStock?: string | null;
  pendienteJustificarStock?: string | null;
  consumoSobreEntregadoStock?: string | null;
}
export interface PrevistosOt {
  ot: { id: string; codigo: string; estado: string };
  editable: boolean;
  previstos: PrevistoOt[];
  noPrevistos: NoPrevistoOt[];
}
/** Lo que el OPERARIO ve de su línea en /api/mis-ots/:id (sólo lectura). */
export interface PrevistoOperario { id: string; articulo: { codigo: string; descripcion: string }; cantidad: string; unidad: string; instrucciones: string | null }

/** F10.3 — una OT de la jornada del operario (`GET /api/mis-ots?vista=jornada`). Estado EFECTIVO. */
export interface MiTrabajo {
  id: string;
  codigo: string;
  estado: string;
  fechaPlan: string;
  fechaLimite: string;
  centroCodigo: string;
  centroDescripcion: string;
  alcance: string;
  /** Instante ISO UTC real (F10.0); null = legado sin hora. */
  inicioProgramadoEn: string | null;
  duracionMin: number;
  fechaLocal: string;
  salaDescripcion: string;
  /** Sobre SUS líneas: hechas = realizadas, no conformes o no aplicables (misma frontera que F10.1). */
  misTareas: { total: number; hechas: number; noConformes: number };
  incidenciasAbiertas: number;
}

/** F10.3 — una tarea del operario (`GET /api/mis-ots/:id`), sólo lectura. */
export interface MiTarea {
  id: string;
  orden: number;
  ubicacionCodigo: string;
  ubicacionRuta: string;
  ubicacionDescripcion: string;
  descripcionTarea: string | null;
  tipoCodigo: string;
  tipoDescripcion: string;
  procedimiento: { nombre: string; url: string | null } | null;
  incidenciasAbiertas: number;
  estado: string;
  materialesPrevistos: PrevistoOperario[];
  /** F14.2 — revisión (último evento) y NC que la cubre: lo mismo que lleva el paquete offline. */
  revisionBase: string | null;
  tieneNc: boolean;
  ncAsociada: NcAsociadaLinea | null;
}

export interface MiTrabajoDetalle extends MiTrabajo {
  observaciones: string | null;
  lineas: MiTarea[];
  /** F14.2 — documentos de la OT y de SUS tareas (los mismos que su paquete offline). */
  documentos: PaqueteDocumentoOffline[];
}

// ---------------------------------------------------------------- entregas y devoluciones logísticas (FASE 9.6-B2)
//  Entregar y devolver NO mueven stock: el único movimiento es el consumo validado (9.6-A).

export interface DevolucionMaterialOt {
  id: string;
  cantidad: string;
  unidad: RefUnidadStock;
  cantidadStock: string;
  observaciones: string | null;
  registradoPor: { id: string; nombre: string };
  registradoEn: string;
}
export interface EntregaMaterialOt {
  id: string;
  idLinea: string;
  estado: 'ACTIVA' | 'ANULADA';
  previsto: { id: string; codigo: string; descripcion: string; idArticulo: string } | null;
  articulo: { id: string; codigo: string; codigoNegocio?: string; descripcion: string; unidadStock: RefUnidadStock; unidades: (RefUnidadStock & { factor: string })[] };
  almacen: { id: string; codigo: string; codigoNegocio?: string; descripcion: string };
  lote: { id: string; codigo: string; fechaCaducidad: string | null; caducado: boolean } | null;
  cantidad: string;
  unidad: RefUnidadStock;
  cantidadStock: string;
  ciclo: { id: string; desde: string } | null;
  observaciones: string | null;
  registradoPor: { id: string; nombre: string };
  registradoEn: string;
  anulada: { en: string; por: string; motivo: string } | null;
  devueltoStock: string;
  maximoDevolvibleStock: string;
  consumidoGrupoStock: string;
  devoluciones: DevolucionMaterialOt[];
  version: number;
}
export interface GrupoLogisticoOt {
  idLinea: string;
  articulo: { id: string; codigo: string; codigoNegocio?: string; descripcion: string };
  almacen: { id: string; codigo: string; codigoNegocio?: string };
  lote: { id: string; codigo: string } | null;
  unidadStock: string;
  entregadaStock: string;
  consumidaValidadaStock: string;
  devueltaStock: string;
  pendienteJustificarStock: string;
  consumoSobreEntregadoStock: string;
}
export interface EntregasMaterialOt {
  ot: { id: string; codigo: string; estado: string };
  editable: boolean;
  puedeDevolver: boolean;
  entregas: EntregaMaterialOt[];
  grupos: GrupoLogisticoOt[];
}

// ---------------------------------------------------------------- F13.1 · identidad externa e importación ERP

export interface SistemaExterno {
  id: string;
  codigo: string;
  nombre: string;
  tipo: string | null;
  activo: boolean;
  /** Maestro vigente de ARTICULOS (compatibilidad F13.1). */
  esMaestro: boolean;
  /** F13.5-A: dominios de los que es maestro vigente. */
  dominiosMaestro: DominioMaestro[];
  plantillaColumnas: Record<string, string> | null;
  nReferencias: number;
  actualizadoEn: string;
}

export type TipoCodigoArticulo = 'EAN8' | 'EAN13' | 'UPCA' | 'GTIN14' | 'CODE128' | 'CODE39' | 'QR' | 'DATAMATRIX' | 'OTRO';

export interface ReferenciaExterna {
  id: string;
  sistema: { id: string; codigo: string; nombre: string; esMaestro: boolean };
  codigoExterno: string;
  origen: 'IMPORTACION' | 'MANUAL';
  importacion: string | null;
  activo: boolean;
  creadoEn: string;
  desactivadoEn: string | null;
  motivoDesactivacion: string | null;
}

export interface CodigoBarrasArticulo {
  id: string;
  tipo: TipoCodigoArticulo;
  valor: string;
  valorNormalizado: string;
  principal: boolean;
  unidad: { id: string; codigo: string } | null;
  descripcion: string | null;
  origen: 'IMPORTACION' | 'MANUAL';
  sistema: string | null;
  activo: boolean;
  creadoEn: string;
}

export interface IdentidadArticulo {
  codigoNegocio: string;
  tokenNeatos: string;
  sistemaCodigoNegocio: string | null;
  referencias: ReferenciaExterna[];
  codigos: CodigoBarrasArticulo[];
  historialCodigoNegocio: CambioCodigoNegocio[];
}

// ---------------------------------------------------------------- F13.5-A · identidad de negocio y maestro por dominio

export type DominioMaestro = 'ARTICULOS' | 'FAMILIAS' | 'ALMACENES' | 'UNIDADES';
export const ETIQUETA_DOMINIO: Readonly<Record<DominioMaestro, string>> = {
  ARTICULOS: 'Artículos', FAMILIAS: 'Familias', ALMACENES: 'Almacenes', UNIDADES: 'Unidades',
};
export interface CambioCodigoNegocio {
  antes: string; despues: string; sistemaAntes: string | null; sistemaDespues: string | null;
  origen: string | null; motivo: string | null; usuario: string; cambiadoEn: string;
}
export interface VigenciaMaestro {
  id: string; dominio: DominioMaestro; sistema: { id: string; codigo: string; nombre: string };
  desde: string; hasta: string | null; asignadoPor: string; motivo: string; excepcion: boolean; motivoExcepcion: string | null;
  cobertura: Record<string, unknown> | null;
}
export interface MaestrosPorDominio {
  dominios: { dominio: DominioMaestro; vigente: VigenciaMaestro | null; disponible: boolean }[];
  historico: VigenciaMaestro[];
}
export interface MuestraPreviewMaestro { id: string; codigo: string; codigoNegocio: string; codigoNuevo?: string; descripcion: string; referenciaInactiva?: boolean }
/** Preview obligatorio de un cambio de maestro (lo calcula la base). */
export interface PreviewMaestro {
  dominio: DominioMaestro;
  soportado: boolean;
  sistemaActual: { id: string; codigo: string } | null;
  sistemaNuevo: { id: string; codigo: string; nombre: string; activo: boolean };
  /** Impiden el cambio siempre (colisiones, sistema inactivo, sin cambio, dominio no disponible). */
  bloqueos: string[];
  /** Sólo un ADMIN puede seguir, con motivo (cobertura incompleta, referencias inactivas, reutilizaciones, alias). */
  requiereExcepcion: boolean;
  motivosExcepcion: string[];
  huella: string;
  cambios: number;
  total?: number; conCodigo?: number; sinCodigo?: number; referenciasInactivas?: number; colisiones?: number; reutilizaciones?: number;
  aliasConflictivos?: number; aliasRedundantes?: number;
  muestraSinCodigo?: MuestraPreviewMaestro[]; muestraColisiones?: MuestraPreviewMaestro[];
  muestraReutilizaciones?: MuestraPreviewMaestro[]; muestraCambios?: MuestraPreviewMaestro[];
  muestraAlias?: { codigoExterno: string; destino: string }[];
}

export interface AliasExterno {
  id: string;
  codigoExterno: string;
  destino: { id: string; codigo: string; descripcion: string; tipo?: string };
}

// ---------------------------------------------------------------- F13.8 · importaciones maestras (un solo motor)

export type TipoMaestro = 'UNIDADES' | 'FAMILIAS' | 'ALMACENES' | 'ARTICULOS';
export type ModoImportacion = 'UPSERT' | 'SOLO_ALTAS' | 'SINCRONIZACION_COMPLETA';
export type ClasificacionImportacion = 'CREAR' | 'ACTUALIZAR' | 'SIN_CAMBIOS' | 'ERROR' | 'CONFLICTO' | 'REACTIVAR' | 'REASIGNAR' | 'DESACTIVAR'
  | 'POSIBLE_COINCIDENCIA' | 'IGNORADA';
/** Error o aviso con código estable y mensaje. */
export interface ProblemaImportacion { codigo: string; mensaje: string }

export interface CampoImportable {
  campo: string;
  etiqueta: string;
  obligatorio: boolean;
  codigo: boolean;
  multiple: boolean;
  ayuda: string;
  valores: string[] | null;
}
export interface TipoImportable {
  tipo: TipoMaestro;
  etiqueta: string;
  campos: CampoImportable[];
  modos: ModoImportacion[];
  dependencias: string[];
  reglas: string[];
  plantilla: { nombre: string; version: string };
  maxFilas: number;
}

export interface PreviewFicheroImportacion {
  formato: 'CSV' | 'XLSX';
  cabeceras: string[];
  muestra: string[][];
  totalFilas: number;
  mapeoSugerido: Record<string, string>;
  campos: CampoImportable[];
  avisos: string[];
  /** Versión de la plantilla oficial de NeatOS, si el fichero es una (NeatOS_Articulos_v1…). */
  plantilla: string | null;
  hashSha256: string;
  mismoFicheroAplicado: string | null;
}

export interface ImportacionMaestra {
  id: string;
  numero: string;
  tipo: TipoMaestro;
  etiqueta: string;
  /** null = NeatOS estándar. */
  sistema: { id: string; codigo: string; nombre: string } | null;
  modo: ModoImportacion;
  estado: 'PREVIEW' | 'APLICADA' | 'CANCELADA';
  nombreArchivo: string;
  formato: 'CSV' | 'XLSX';
  hashSha256: string;
  tamanoBytes: number;
  versionPlantilla: string | null;
  mapeo: Record<string, string>;
  contadores: Record<ClasificacionImportacion, number> & { total: number };
  avisos: ProblemaImportacion[];
  /** ERROR + CONFLICTO + POSIBLE_COINCIDENCIA: bloquean el aplicar salvo exclusión explícita. */
  bloqueadas: number;
  aplicables: number;
  requiereConfirmacionReforzada: boolean;
  umbralDesactivacion: number | null;
  decisionesPendientes: number;
  mismoFicheroAplicado: string | null;
  filasExcluidas: number;
  resultado: { APLICADA: number; OMITIDA: number; EXCLUIDA: number } | null;
  caducaEn: string;
  caducada: boolean;
  creadoPor: string;
  creadoEn: string;
  aplicadoPor: string | null;
  aplicadoEn: string | null;
  canceladoPor: string | null;
  canceladoEn: string | null;
}

/** Acción propuesta de una fila (lo que se aplicará). Cada maestro usa las piezas que le corresponden. */
export interface AccionImportacion {
  via?: string;
  crear?: { descripcion?: string; codigo?: string; familia?: string; unidad?: string; tipo?: string; centro?: string | null };
  codigo?: { antes: string; despues: string };
  descripcion?: { antes: string; despues: string };
  unidad?: { antes: { codigo: string }; despues: { codigo: string } };
  familia?: { antes: { codigo: string }; despues: { codigo: string } };
  activo?: { antes: boolean; despues: boolean };
  extra?: Record<string, { antes: unknown; despues: unknown }>;
  referencia?: string;
  codigos?: { tipo: TipoCodigoArticulo; valor: string }[];
  /** REASIGNAR: la referencia de la entidad de baja que queda histórica. */
  reasignar?: { idReferencia: string; idArticulo: string; codigoArticulo: string };
  /** Código de negocio que tendrá (maestro o NeatOS estándar). `antes` null = alta. */
  codigoNegocio?: { antes: string | null; despues: string };
}

export interface FilaImportacion {
  id: string;
  numero: number | null;
  codigo: string | null;
  clasificacion: ClasificacionImportacion;
  entidad: { id: string; codigo: string; codigoNegocio: string; descripcion: string } | null;
  originales: Record<string, string>;
  datos: Record<string, unknown> | null;
  candidatos: { id: string; codigo: string; codigoNegocio?: string; descripcion: string }[] | null;
  decision: { decision: 'ACEPTAR' | 'CREAR' | 'IGNORAR' | 'REACTIVAR' | 'REASIGNAR'; idEntidad: string | null } | null;
  /** Decisiones que admite la fila (vacío = ninguna). */
  decisiones: string[];
  accion: AccionImportacion | null;
  avisos: ProblemaImportacion[];
  errores: ProblemaImportacion[];
  excluida: boolean;
  resultado: 'APLICADA' | 'OMITIDA' | 'EXCLUIDA' | null;
}

// ---------------------------------------------------------------- F13.5-B · exportaciones de inventario

export type TipoExportacion = 'STOCK_ACTUAL' | 'STOCK_A_FECHA' | 'MOVIMIENTOS' | 'CONSUMOS';
export type FormatoExportacion = 'CSV' | 'XLSX';
export interface OpcionesCsv { separador: ';' | ',' | '	' | '|'; decimal: ',' | '.'; bom: boolean }
export interface FiltrosExportacion {
  idCentro?: string;
  idAlmacen?: string;
  idArticulo?: string;
  idFamilia?: string;
  idLote?: string;
  tipos?: string[];
  tipoDocumento?: string;
  exportado?: 'SI' | 'NO';
}
export interface DestinoExportacion { id: string | null; codigo: string; nombre: string; maestroDe?: string[] }
export interface OpcionesExportacion {
  centros: { id: string; codigo: string; descripcion: string }[];
  familias: { id: string; codigo: string; descripcion: string }[];
  destinos: DestinoExportacion[];
  tipos: { tipo: string; etiqueta: string; disponible: boolean; motivo?: string }[];
  csv: OpcionesCsv;
}
export interface ErrorExportacion {
  codigo: 'ERROR_REFERENCIA_DESTINO_FALTANTE';
  entidad: 'ARTICULO' | 'ALMACEN' | 'FAMILIA' | 'UNIDAD';
  /** Columna del layout que exige el código del destino. */
  campo: string;
  id: string;
  codigoNegocio: string;
  descripcion: string;
  mensaje: string;
  filas: number;
}
export interface AvisoExportacion { codigo: string; filas: number; mensajes: string[] }
export type ValoresExportacion = Record<string, string | null>;
export interface PreviewExportacion {
  tipo: TipoExportacion;
  destino: DestinoExportacion;
  resolucion: Record<string, unknown>;
  periodo: { desde: string; hasta: string } | null;
  periodoTexto: string;
  filtros: FiltrosExportacion;
  filtrosTexto: [string, string][];
  formato: FormatoExportacion;
  versionLayout: string;
  columnas: string[];
  totalFilas: number;
  filasValidas: number;
  filasConError: number;
  errores: ErrorExportacion[];
  avisos: AvisoExportacion[];
  totales: { unidad: string; cantidad: string; filas: number }[];
  muestra: { valores: ValoresExportacion; conError: boolean }[];
  huella: string;
}
export interface ExportacionResumen {
  id: string;
  numero: string;
  tipo: TipoExportacion;
  destino: DestinoExportacion;
  periodo: { desde: string; hasta: string } | null;
  formato: FormatoExportacion;
  estado: 'GENERADA' | 'ANULADA';
  numFilas: number;
  numExcluidas: number;
  nombreFichero: string;
  creadoPor: { id: string; nombre: string };
  creadoEn: string;
  reexportacionDe: { id: string; numero: string } | null;
  /** F13.6: cierre del que sale (stock a fecha). */
  cierre?: { id: string; codigo: string; version: number } | null;
}
export interface ExportacionDetalle extends ExportacionResumen {
  versionLayout: string;
  filtros: FiltrosExportacion;
  filtrosTexto: [string, string][];
  opciones: Partial<OpcionesCsv>;
  resolucion: Record<string, unknown>;
  resumen: { usuario?: string; avisos?: AvisoExportacion[]; excluidas?: ErrorExportacion[]; totales?: { unidad: string; cantidad: string; filas: number }[] };
  sha256: string;
  tamanoBytes: number;
  huellaDatos: string;
  ambito: { global: boolean; centros: { id: string; codigo: string; descripcion: string }[] };
  anulacion: { por: string; en: string; motivo: string } | null;
  reexportaciones: { id: string; numero: string; creadoEn: string; estado: string }[];
  columnas: string[];
  muestra: ValoresExportacion[];
  puedeAnular: boolean;
}

// ---------------------------------------------------------------- F13.6 · stock histórico, kárdex y cierres

export type ClaseMovimiento = 'ENTRADA' | 'SALIDA' | 'TRASLADO_ENTRADA' | 'TRASLADO_SALIDA' | 'CONSUMO_PT' | 'CONSUMO_OT' | 'AJUSTE_POSITIVO'
  | 'AJUSTE_NEGATIVO' | 'DEVOLUCION' | 'RECTIFICACION' | 'OTROS';
export interface RefCodigo { codigo: string; descripcion: string }
export interface OpcionesHistorico {
  centros: ({ id: string } & RefCodigo)[];
  familias: ({ id: string } & RefCodigo)[];
  sistemas: ({ id: string } & RefCodigo)[];
  hoy: string;
}
export interface FilaStockHistorico {
  idArticulo: string;
  codigoArticulo: string;
  descripcion: string;
  familia: string;
  idAlmacen: string;
  codigoAlmacen: string;
  almacen: string;
  centro: RefCodigo | null;
  centroDesconocido: boolean;
  idLote: string | null;
  lote: string | null;
  caducidad: string | null;
  idSerie: string | null;
  serie: string | null;
  unidad: string;
  stock: string;
}
export interface StockHistorico {
  fecha: string;
  corteTexto: string;
  filtrosTexto: [string, string][];
  total: number;
  pagina: number;
  porPagina: number;
  filas: FilaStockHistorico[];
  totales: { unidad: string; cantidad: string; filas: number }[];
  avisos: string[];
}
export interface FilaKardex {
  idMovimiento: string;
  fecha: string;
  fechaDocumento: string | null;
  clase: ClaseMovimiento;
  claseTexto: string;
  tipoOperacion: string;
  operacion: string;
  idOperacion: string;
  documento: string | null;
  rectificaA: string | null;
  almacen: { id: string } & RefCodigo;
  contraparte: ({ id: string } & RefCodigo) | null;
  contraparteFueraDeAmbito: boolean;
  lote: string | null;
  serie: string | null;
  entrada: string | null;
  salida: string | null;
  saldoAnterior: string;
  movimiento: string;
  saldo: string;
  usuario: string;
  pt: string | null;
  ot: string | null;
  centro: RefCodigo | null;
  centroDesconocido: boolean;
  exportaciones: string[];
}
export interface Kardex {
  articulo: { id: string; codigo: string; descripcion: string; unidad: string };
  filtrosTexto: [string, string][];
  desde: string;
  hasta: string;
  periodoTexto: string;
  saldoInicial: string;
  entradas: string;
  salidas: string;
  saldoFinal: string;
  movimientos: number;
  filas: FilaKardex[];
}
export interface ConciliacionResumen { numero: number; diferencias: number; sinCorrespondencia: number; soloNeatos: number; cuadran: number }
export interface CierreResumen {
  id: string;
  codigo: string;
  version: number;
  estado: 'VIGENTE' | 'SUSTITUIDO';
  periodo: string;
  desde: string;
  hasta: string;
  ambitoTexto: string;
  creadoPor: string;
  creadoEn: string;
  numLineas: number;
  numIncidencias: number;
  incidencias: Record<string, number>;
  conciliacion: ConciliacionResumen | null;
  afectado: boolean | null;
}
export interface PorUnidadCierre {
  unidad: string;
  referencias: number;
  lineas: number;
  stockInicial: string;
  entradas: string;
  salidas: string;
  stockFinal: string;
  movimientos: number;
  porClase: Record<string, { entradas: string; salidas: string; movimientos: number }>;
}
export interface SituacionCierre {
  afectado: boolean;
  movimientosNuevos: number;
  nuevosEnPeriodo: { idMovimiento: number; operacion: string; fechaMovimiento: string }[];
  documentosTardiosNuevos: { idMovimiento: number; operacion: string; fechaDocumento: string; fechaMovimiento: string }[];
}
export interface CierreDetalle extends CierreResumen {
  filtros: { idCentro?: string; idAlmacen?: string };
  ambito: { global: boolean; centros: RefCodigo[]; almacenes: RefCodigo[] };
  motivo: string | null;
  regeneraA: string | null;
  sustituidoPor: { id: string; version: number } | null;
  kpi: { lineas: number; referencias: number; movimientos: number; documentosTardios: number; centrosDesconocidos: number; periodoEnCurso: boolean;
    porUnidad: PorUnidadCierre[] };
  situacion: SituacionCierre | null;
  versiones: { id: string; version: number; estado: string; creadoEn: string; creadoPor: string; motivo: string | null }[];
  incidenciasDetalle: { tipo: string; mensaje: string; operacion: string | null; idOperacion: string | null; idMovimiento: string | null;
    fechaDocumento: string | null; fechaMovimiento: string | null }[];
  conciliaciones: { id: string; numero: number; sistema: string; origen: string; nombreFichero: string | null; creadoEn: string; creadoPor: string;
    resumen: Partial<ConciliacionResumen> & { lineas?: number } }[];
  eventos: { tipo: string; datos: Record<string, unknown>; usuario: string; en: string }[];
  exportaciones: { id: string; numero: string; estado: string; creadoEn: string }[];
}
export interface LineaCierre {
  orden: number;
  idArticulo: string;
  codigoArticulo: string;
  descripcion: string;
  idAlmacen: string;
  codigoAlmacen: string;
  almacen: string;
  idLote: string | null;
  lote: string | null;
  idSerie: string | null;
  serie: string | null;
  unidad: string;
  centro: string | null;
  centroDesconocido: boolean;
  stockInicial: string;
  entradas: string;
  salidas: string;
  stockFinal: string;
  movimientos: number;
  desglose: { clase: ClaseMovimiento; texto: string; entradas: string; salidas: string; movimientos: number }[];
}
export type EstadoConciliacion = 'CUADRA' | 'DIFERENCIA' | 'SIN_CORRESPONDENCIA' | 'SOLO_NEATOS';
export interface Conciliacion {
  id: string;
  numero: number;
  cierre: { id: string; codigo: string; version: number };
  sistema: string;
  origen: 'MANUAL' | 'FICHERO';
  nombreFichero: string | null;
  creadoEn: string;
  creadoPor: string;
  resumen: Partial<ConciliacionResumen> & { lineas?: number; porUnidad?: { unidad: string; neatos: number; erp: number; diferencia: number }[] };
  lineas: { orden: number; codigoArticulo: string | null; codigoAlmacen: string | null; lote: string | null; stockErp: string | null;
    stockNeatos: string | null; diferencia: string | null; unidad: string | null; estado: EstadoConciliacion; mensaje: string }[];
  filasOmitidas?: number;
}

/**
 * PC-v2.2-A — configuración de los indicadores del portal (sólo ADMIN, `portal:configurar`). Un nivel: el cliente
 * (`idCentro` null) o uno de sus centros. `propio` = lo que guarda este nivel (null = hereda); `heredado` = lo que
 * vendría de arriba. Los esenciales se ven siempre.
 */
export type ModuloIndicadorPortal = 'INICIO' | 'CALENDARIO' | 'CALIDAD';
export interface IndicadorPortalConfigurado {
  id: string;
  nombre: string;
  descripcion: string;
  modulo: ModuloIndicadorPortal;
  grupo: string;
  tipo: 'CONTADOR' | 'PORCENTAJE' | 'DURACION' | 'DISTRIBUCION' | 'SERIE' | 'TABLA';
  esencial: boolean;
  visiblePorDefecto: boolean;
  filtrablePorCentro: boolean;
  grafico: boolean;
  exportable: boolean;
  /** PC-v2.2-C1: cifras que enseña un gráfico; sólo se ve si ellas también se ven en este nivel. */
  dependeDe?: string[];
  propio: boolean | null;
  heredado: { visible: boolean; de: 'GENERAL' | 'CLIENTE' };
  /** Cifras de `dependeDe` ocultas en este nivel (con lo guardado). */
  ocultoPor?: string[];
  visible: boolean;
}
export interface ConfiguracionIndicadoresPortal {
  idCliente: string;
  idCentro: string | null;
  nivel: 'CLIENTE' | 'CENTRO';
  version: string;
  indicadores: IndicadorPortalConfigurado[];
}
